import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Box,
  ChevronDown,
  ChevronRight,
  Code2,
  FunctionSquare,
  GripVertical,
  List,
  Loader2,
  MessageSquare,
  Play,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { SimpleSelect } from '@/components/ui/simple-select'
import { Textarea } from '@/components/ui/textarea'
import type { InputParameter, MethodSignature } from '@/db/schema/problems'
import {
  DEFAULT_TEST_TYPE,
  TEST_TYPE,
  type TestType,
} from '@/lib/constants/test-types'
import { trpc } from '@/lib/trpc'
import { getReferencedTemplates, parseType } from '@/lib/type-system'
import { cn } from '@/lib/utils'
import { TestResultsSection, type TestRunSummary } from './approach-code-editor'
import {
  type CustomCodeData,
  CustomTestLogicSection,
  type SelectedTemplate,
} from './custom-test-logic-section'
import { MethodSignaturesEditor } from './method-signatures-editor'
import { StarterCodeWidget } from './starter-code-widget'
import { type AvailableTemplate, TypeSelector } from './type-selector'

const MonacoEditorLazy = lazy(
  () =>
    import('@monaco-editor/react') as Promise<{
      default: typeof import('@monaco-editor/react')['default']
    }>,
)

const ALL_LANGUAGES = [
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
] as const

export type { InputParameter }

export interface TestCase {
  id?: string
  inputs: Record<string, string> // parameterName -> value (as string for editing)
  expectedOutput: string
  expectedInputsAfter?: Record<string, string> // For in-place modifications: expected values after call
  order: number
  intent?: string // Optional: Short rationale explaining what makes this test unique
  showAsExample?: boolean // Include this test case in examples?
  imageFilename?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
  displayOverrideStale?: boolean
}

// Operation in a class-based test
export interface OperationItem {
  type?: 'operation' // Optional for backward compatibility
  method: string
  args: string // JSON array as string for editing
  expected: string // JSON value as string for editing
  expectsError?: boolean // If true, operation should raise an exception
  propertyToCheck?: string // Property to verify after method call (e.g., "matrix", "capacity")
  expectedPropertyValue?: string // Expected value of that property (JSON string)
}

// Comment in a class-based test (annotates the operation list only)
export interface CommentItem {
  type: 'comment'
  text: string // Comment text (multi-line supported with \n separators)
}

// Union type for operations array items
export type Operation = OperationItem | CommentItem

// Type guards
export function isComment(item: Operation): item is CommentItem {
  return item.type === 'comment'
}

export function isOperation(item: Operation): item is OperationItem {
  return item.type !== 'comment'
}

// Class-based test case
export interface ClassTestCase {
  id?: string
  constructorArgs?: string // JSON array as string for editing
  operations: Operation[]
  order: number
  intent?: string
  showAsExample?: boolean // Include this test case in examples?
  imageFilename?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
  displayOverrideStale?: boolean
}

// Raw test case (admin-authored scripts per language)
export interface RawTestCase {
  id?: string
  scripts: Record<string, string> // language -> script content
  exampleDisplay?: string // Formatted text for example card
  order: number
  intent?: string
  showAsExample?: boolean
}

export type { TestType }

// =============================================================================
// Function Spec Types (for comparator and predicate parameters)
// =============================================================================

/**
 * Comparator function spec - for heap/priority queue operations
 */
export interface ComparatorSpec {
  type: 'comparator'
  operator: '<' | '>' | '<=' | '>='
}

/**
 * Predicate function spec - for oracle/callback functions
 */
export interface PredicateSpec {
  type: 'predicate'
  operator: '<' | '>' | '<=' | '>=' | '==' | '!='
  threshold: number
}

export type FunctionSpec = ComparatorSpec | PredicateSpec

const COMPARATOR_OPERATORS = ['<', '>', '<=', '>='] as const
const PREDICATE_OPERATORS = ['<', '>', '<=', '>=', '==', '!='] as const

/**
 * Parse a JSON string into a FunctionSpec, or return null if invalid
 */
function parseFunctionSpec(jsonStr: string): FunctionSpec | null {
  if (!jsonStr || !jsonStr.trim()) return null
  try {
    const parsed = JSON.parse(jsonStr)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (parsed.type === 'comparator' || parsed.type === 'predicate') {
      return parsed as FunctionSpec
    }
    return null
  } catch {
    return null
  }
}

/**
 * Escape a string for text mode serialization.
 * Handles both newlines and literal backslashes to enable safe round-tripping.
 * - Backslashes are doubled: \ → \\
 * - Newlines are escaped: (actual newline) → \n
 */
function escapeForTextMode(str: string): string {
  // First escape backslashes, then escape newlines
  return str.replace(/\\/g, '\\\\').replace(/\n/g, '\\n')
}

/**
 * Unescape a string from text mode serialization.
 * Reverses the escaping done by escapeForTextMode.
 * - \n → actual newline
 * - \\ → single backslash
 * Uses single-pass replacement to handle sequences correctly.
 */
function unescapeFromTextMode(str: string): string {
  return str.replace(/\\(.)/g, (_, char: string) => {
    if (char === 'n') return '\n'
    if (char === '\\') return '\\'
    // For any other escaped character, return it as-is (handles edge cases)
    return char
  })
}

/**
 * Serialize a FunctionSpec to JSON string
 */
function serializeFunctionSpec(spec: FunctionSpec): string {
  return JSON.stringify(spec)
}

/**
 * Create a default function spec based on type
 */
function createDefaultFunctionSpec(
  specType: 'comparator' | 'predicate',
): FunctionSpec {
  if (specType === 'comparator') {
    return { type: 'comparator', operator: '<' }
  }
  return { type: 'predicate', operator: '>=', threshold: 0 }
}

/**
 * Normalize function name input to kebab-case slug format.
 * - Converts spaces to hyphens
 * - Converts underscores to hyphens
 * - Removes or converts non-alphanumeric characters to hyphens
 * - Converts to lowercase
 * - Removes consecutive hyphens
 * - Trims leading hyphens only (allows trailing for typing flow)
 */
function normalizeFunctionNameInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_\s]+/g, '-') // Replace underscores and spaces with hyphens
    .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric (except hyphens) with hyphens
    .replace(/-+/g, '-') // Remove consecutive hyphens
    .replace(/^-+/, '') // Trim leading hyphens only (trailing allowed for typing)
}

// =============================================================================
// FunctionNamesEditor Component
// =============================================================================

function FunctionNamesEditor({
  functionNames,
  onChange,
}: {
  functionNames: string[]
  onChange: (names: string[]) => void
}) {
  const [inputValue, setInputValue] = useState('')

  const addName = () => {
    const normalized = normalizeFunctionNameInput(inputValue).replace(/-+$/, '')
    if (!normalized) return
    if (functionNames.includes(normalized)) {
      setInputValue('')
      return
    }
    onChange([...functionNames, normalized])
    setInputValue('')
  }

  const removeName = (index: number) => {
    onChange(functionNames.filter((_, i) => i !== index))
  }

  const moveName = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= functionNames.length) return
    const updated = [...functionNames]
    ;[updated[index], updated[target]] = [updated[target], updated[index]]
    onChange(updated)
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Acceptable Function Names</h3>
      {functionNames.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {functionNames.map((name, i) => (
            <span
              key={name}
              className="bg-muted inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-sm"
            >
              {i === 0 && (
                <span className="text-muted-foreground mr-0.5 text-[10px] font-sans font-medium uppercase">
                  preferred
                </span>
              )}
              {name}
              {functionNames.length > 1 && i > 0 && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground -mr-0.5 ml-0.5"
                  onClick={() => moveName(i, -1)}
                  title="Move left"
                >
                  ‹
                </button>
              )}
              {functionNames.length > 1 && i < functionNames.length - 1 && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground -mr-0.5 ml-0.5"
                  onClick={() => moveName(i, 1)}
                  title="Move right"
                >
                  ›
                </button>
              )}
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive -mr-0.5 ml-0.5"
                onClick={() => removeName(i)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={inputValue}
          onChange={(e) =>
            setInputValue(normalizeFunctionNameInput(e.target.value))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addName()
            }
          }}
          placeholder="Add function name slug..."
          className="max-w-xs text-sm font-mono"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addName}
          disabled={!inputValue.trim()}
        >
          Add
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">
        First name is used in code templates. Slug-derived name is always
        accepted. Use hyphenated format (e.g., "spiral" or "three-sum").
      </p>
    </div>
  )
}

// =============================================================================
// FunctionInput Component
// =============================================================================

interface FunctionInputProps {
  value: string
  onChange: (value: string) => void
  paramName: string
}

/**
 * Specialized input component for function-type parameters.
 * Provides a user-friendly UI for creating comparator and predicate specs.
 */
function FunctionInput({ value, onChange, paramName }: FunctionInputProps) {
  const spec = parseFunctionSpec(value)
  const specType = spec?.type || 'predicate'

  const handleTypeChange = (newType: 'comparator' | 'predicate') => {
    const newSpec = createDefaultFunctionSpec(newType)
    onChange(serializeFunctionSpec(newSpec))
  }

  const handleOperatorChange = (newOperator: string) => {
    if (spec) {
      const updatedSpec = { ...spec, operator: newOperator } as FunctionSpec
      onChange(serializeFunctionSpec(updatedSpec))
    } else {
      const newSpec: PredicateSpec = {
        type: 'predicate',
        operator: newOperator as PredicateSpec['operator'],
        threshold: 0,
      }
      onChange(serializeFunctionSpec(newSpec))
    }
  }

  const handleThresholdChange = (newThreshold: number) => {
    if (spec?.type === 'predicate') {
      const updatedSpec: PredicateSpec = { ...spec, threshold: newThreshold }
      onChange(serializeFunctionSpec(updatedSpec))
    } else {
      const newSpec: PredicateSpec = {
        type: 'predicate',
        operator: '>=',
        threshold: newThreshold,
      }
      onChange(serializeFunctionSpec(newSpec))
    }
  }

  // If no valid spec yet, show empty state with create button
  if (!spec) {
    return (
      <div className="border-border bg-muted/30 flex items-center gap-2 rounded-lg border p-3">
        <span className="text-muted-foreground text-sm">
          No function defined
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange(
              serializeFunctionSpec(createDefaultFunctionSpec('predicate')),
            )
          }
        >
          <Plus className="mr-1 h-3 w-3" />
          Create Predicate
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange(
              serializeFunctionSpec(createDefaultFunctionSpec('comparator')),
            )
          }
        >
          <Plus className="mr-1 h-3 w-3" />
          Create Comparator
        </Button>
      </div>
    )
  }

  return (
    <div className="border-border bg-muted/30 rounded-lg border p-3 space-y-3">
      <div className="flex items-center gap-3">
        {/* Function Type Selector */}
        <div className="flex-shrink-0">
          <label className="text-muted-foreground mb-1 block text-xs">
            Type
          </label>
          <SimpleSelect
            value={specType}
            onChange={(v) => handleTypeChange(v as 'comparator' | 'predicate')}
            options={[
              { value: 'predicate', label: 'Predicate (oracle)' },
              { value: 'comparator', label: 'Comparator (heap)' },
            ]}
            width="md"
            className="text-sm"
          />
        </div>

        {/* Operator Selector */}
        <div className="flex-shrink-0">
          <label className="text-muted-foreground mb-1 block text-xs">
            Operator
          </label>
          <SimpleSelect
            value={spec.operator}
            onChange={handleOperatorChange}
            options={
              specType === 'predicate'
                ? PREDICATE_OPERATORS.map((op) => ({ value: op, label: op }))
                : COMPARATOR_OPERATORS.map((op) => ({ value: op, label: op }))
            }
            width="sm"
            className="text-sm font-mono"
          />
        </div>

        {/* Threshold Input (only for predicates) */}
        {specType === 'predicate' && (
          <div className="flex-shrink-0">
            <label className="text-muted-foreground mb-1 block text-xs">
              Threshold
            </label>
            <Input
              type="number"
              value={(spec as PredicateSpec).threshold ?? 0}
              onChange={(e) => handleThresholdChange(Number(e.target.value))}
              className="w-24 text-sm font-mono"
            />
          </div>
        )}

        {/* Clear button */}
        <div className="flex-shrink-0 self-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="text-xs">
        <span className="text-muted-foreground">Generates: </span>
        <code className="bg-muted rounded px-1.5 py-0.5 font-mono">
          {specType === 'predicate'
            ? `lambda t: t ${spec.operator} ${(spec as PredicateSpec).threshold}`
            : `lambda x, y: x ${spec.operator} y`}
        </code>
      </div>

      {/* Help text */}
      <p className="text-muted-foreground text-xs">
        {specType === 'predicate'
          ? `This predicate will be passed to "${paramName}" and return True when the input ${spec.operator} ${(spec as PredicateSpec).threshold}.`
          : `This comparator will be passed to "${paramName}" for comparing two values.`}
      </p>
    </div>
  )
}

// =============================================================================
// CommentRow Component - Renders a comment item in the operations list
// =============================================================================

interface CommentRowProps {
  text: string
  onTextChange: (text: string) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  isFirst: boolean
  isLast: boolean
}

function CommentRow({
  text,
  onTextChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  isFirst,
  isLast,
}: CommentRowProps) {
  const [isMultiline, setIsMultiline] = useState(text.includes('\n'))
  const hasMultipleLines = text.includes('\n')

  return (
    <div className="border-border bg-amber-50 dark:bg-amber-950/30 flex items-start gap-2 rounded border border-amber-200 dark:border-amber-800 p-2">
      <div className="flex items-center gap-1.5 pt-1">
        <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">
          #
        </span>
      </div>
      <div className="flex-1">
        {isMultiline || hasMultipleLines ? (
          <Textarea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Comment text..."
            className="min-h-[60px] w-full resize-y text-sm"
          />
        ) : (
          <Input
            type="text"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder="Comment text..."
            className="h-8 w-full text-sm"
          />
        )}
      </div>
      <div className="flex items-center gap-1 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsMultiline(!isMultiline)}
          className="h-6 w-6 p-0"
          title={isMultiline ? 'Switch to single line' : 'Switch to multi-line'}
        >
          {isMultiline ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMoveUp}
          disabled={isFirst}
          className="h-6 w-6 p-0"
        >
          ↑
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMoveDown}
          disabled={isLast}
          className="h-6 w-6 p-0"
        >
          ↓
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

// Comparison modes for test output validation
export const COMPARISON_MODES = [
  'exact',
  'set_equality',
  'sorted_equality',
] as const

export type ComparisonMode = (typeof COMPARISON_MODES)[number]

export interface TestConfiguration {
  testType: TestType
  // Function-based fields:
  functionNames?: string[] // Acceptable function name slugs (first = preferred, slug-derived always accepted)
  inputParameters: InputParameter[]
  outputType: string
  testCases: TestCase[]
  // Class-based fields:
  className?: string
  constructorParameters?: InputParameter[]
  methods?: MethodSignature[]
  classTestCases?: ClassTestCase[]
  // Raw test cases:
  rawTestCases?: RawTestCase[]
  // Comparison settings:
  comparisonMode?: ComparisonMode // How to compare outputs (default: "exact")
  tolerance?: number // For approximate comparisons (epsilon)
  approximate?: boolean
  caseInsensitive?: boolean
  // Custom test handler fields:
  templates?: Array<{
    id: string
    name: string
    slug: string
    description: string | null
    order: number
  }>
  customCode?: Record<
    string,
    {
      setupCode: string | null
      verificationCode: string | null
      testCode: string | null
      testExpectedOutput: string | null
      lastValidatedAt: string | null
    }
  >
  // Starter code stubs (language-keyed, auto-generated on save)
  starterCode?: Record<string, string>
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Validates that templates are linked when using a type that references templates.
 * Shows an error toast and returns true if validation fails.
 */
function validateTemplateType(
  newType: string,
  testConfig: TestConfiguration,
): boolean {
  try {
    const expr = parseType(newType)
    const referencedSlugs = getReferencedTemplates(expr)
    if (
      referencedSlugs.length > 0 &&
      (!testConfig.templates || testConfig.templates.length === 0)
    ) {
      toast.error(
        'Please add a template in the Custom Test Logic section before using template types.',
      )
      return true // Validation failed
    }
  } catch {
    // Unparseable type — allow it through, let other validation catch it
  }
  return false // Validation passed
}

// =============================================================================
// ParameterRow Component (Sortable)
// =============================================================================

interface ParameterRowProps {
  id: string
  param: InputParameter & { originalIndex: number }
  testConfig: TestConfiguration
  availableTemplates: AvailableTemplate[]
  mode: 'user-facing' | 'test-only'
  onUpdate: (index: number, updates: Partial<InputParameter>) => void
  onRemove: (index: number) => void
}

function RawTestScriptEditor({
  scripts,
  onChange,
}: {
  scripts: Record<string, string>
  onChange: (scripts: Record<string, string>) => void
}) {
  const existingLanguages = Object.keys(scripts).filter(
    (lang) => scripts[lang] !== undefined,
  )
  const languages =
    existingLanguages.length > 0 ? existingLanguages : ['python']
  const [activeTab, setActiveTab] = useState(languages[0])
  const [addLangOpen, setAddLangOpen] = useState(false)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const isDarkMode =
    typeof window !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false

  const availableToAdd = ALL_LANGUAGES.filter(
    (lang) => !languages.includes(lang.value),
  )

  const handleAddLanguage = (langValue: string) => {
    onChange({ ...scripts, [langValue]: '' })
    setActiveTab(langValue)
    setAddLangOpen(false)
  }

  const handleRemoveLanguage = (langValue: string) => {
    const next = { ...scripts }
    delete next[langValue]
    onChange(next)
    if (activeTab === langValue) {
      setActiveTab(Object.keys(next)[0] || 'python')
    }
  }

  const safeActiveTab = languages.includes(activeTab) ? activeTab : languages[0]

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">Test Script</label>
      <p className="text-muted-foreground text-xs">
        This script runs after the student&apos;s code. Use assertions to verify
        behavior.
      </p>
      <div className="border-border overflow-hidden rounded-md border">
        {/* Tab bar */}
        <div className="bg-muted/30 flex items-center border-b border-border">
          {languages.map((lang) => {
            const label =
              ALL_LANGUAGES.find((l) => l.value === lang)?.label || lang
            const isActive = safeActiveTab === lang
            return (
              <div
                key={lang}
                className={`flex items-center border-r border-border ${
                  isActive ? 'bg-background' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab(lang)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
                {lang !== 'python' && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLanguage(lang)}
                    className="text-muted-foreground hover:text-destructive mr-1 rounded p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )
          })}
          {availableToAdd.length > 0 && (
            <Popover open={addLangOpen} onOpenChange={setAddLangOpen}>
              <PopoverAnchor asChild>
                <button
                  type="button"
                  onClick={() => setAddLangOpen(true)}
                  className="text-muted-foreground hover:text-foreground px-2 py-1.5"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </PopoverAnchor>
              <PopoverContent className="w-40 p-1" align="start">
                {availableToAdd.map((lang) => (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => handleAddLanguage(lang.value)}
                    className="hover:bg-muted w-full rounded px-2 py-1.5 text-left text-sm"
                  >
                    {lang.label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>
        {/* Monaco Editor */}
        <div style={{ height: '250px' }}>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
                Loading editor...
              </div>
            }
          >
            {isClient && (
              <MonacoEditorLazy
                height="100%"
                language={safeActiveTab}
                value={scripts[safeActiveTab] ?? ''}
                theme={isDarkMode ? 'vs-dark' : 'vs'}
                onChange={(val) =>
                  onChange({
                    ...scripts,
                    [safeActiveTab]: val || '',
                  })
                }
                options={{
                  automaticLayout: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  fontFamily:
                    'Menlo, Monaco, Consolas, "Courier New", monospace',
                  tabSize: safeActiveTab === 'javascript' ? 2 : 4,
                }}
              />
            )}
          </Suspense>
        </div>
      </div>
    </div>
  )
}

/**
 * Sortable parameter row — supports drag-and-drop reordering within and
 * between the user-facing and test-only parameter sections.
 */
function ParameterRow({
  id,
  param,
  testConfig,
  availableTemplates,
  mode,
  onUpdate,
  onRemove,
}: ParameterRowProps) {
  const isUserFacing = mode === 'user-facing'
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border-border bg-muted/30 flex items-center gap-3 rounded-lg border p-3"
    >
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground cursor-grab touch-none active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 flex-shrink-0" />
      </button>

      <div className="flex flex-1 gap-3">
        <div className="w-44 flex-shrink-0">
          <Input
            type="text"
            value={param.name}
            onChange={(e) =>
              onUpdate(param.originalIndex, {
                name: e.target.value,
              })
            }
            placeholder={
              isUserFacing ? 'e.g., head, nums' : 'e.g., values, cycleIndex'
            }
            className="w-full text-sm"
          />
        </div>
        <div className="flex-1">
          <TypeSelector
            value={param.type}
            onChange={(newType) => {
              if (validateTemplateType(newType, testConfig)) {
                return
              }
              onUpdate(param.originalIndex, {
                type: newType,
              })
            }}
            availableTemplates={availableTemplates}
            className="text-sm"
          />
        </div>
        {isUserFacing && (
          <label
            className="flex items-center gap-1.5 text-xs"
            title="Check if this parameter is modified in-place by the function"
          >
            <input
              type="checkbox"
              checked={param.inPlaceModified || false}
              onChange={(e) =>
                onUpdate(param.originalIndex, {
                  inPlaceModified: e.target.checked,
                })
              }
              className="h-3.5 w-3.5 rounded border-gray-300"
            />
            <span className="text-muted-foreground whitespace-nowrap">
              in-place
            </span>
          </label>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(param.originalIndex)}
        className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

/** Droppable zone so empty sections can accept drops. */
function DroppableSection({
  id,
  children,
}: {
  id: string
  children: React.ReactNode
}) {
  const { setNodeRef } = useDroppable({ id })
  return <div ref={setNodeRef}>{children}</div>
}

// =============================================================================
// Tests Section
// =============================================================================

interface TestsSectionProps {
  testConfig: TestConfiguration
  onUpdate: (testConfig: TestConfiguration) => void
  onSave?: (
    testConfig: TestConfiguration,
    options?: { regenerateStarterCode?: boolean },
  ) => Promise<void>
  /** Languages whose authored starter-code stub the last save kept. */
  preservedStarterCode?: readonly string[]
  /**
   * A save of this config is in flight somewhere in the editor, possibly one
   * started by an earlier mount of this section. Save and Regenerate wait for
   * it like they wait for each other.
   */
  savePending?: boolean
  problemId?: string
  // New props for test execution:
  problemSlug?: string
  approaches?: Array<{
    id?: string
    name?: string
    solutions?: Array<{
      language: string
      code: string
    }>
  }>
}

function ImageFilenameInput({
  value,
  onChange,
  availableImages,
}: {
  value: string
  onChange: (value: string) => void
  availableImages: string[]
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(value)
  const filtered = availableImages.filter((img) =>
    img.toLowerCase().includes(search.toLowerCase()),
  )

  // Sync search state when external value changes
  useEffect(() => {
    setSearch(value)
  }, [value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              onChange(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            placeholder="Image filename (e.g., tree-check-1.png)"
            className="w-full text-sm"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search images..."
            value={search}
            onValueChange={(val) => {
              setSearch(val)
              onChange(val)
            }}
          />
          <CommandList>
            <CommandEmpty>No images found.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 20).map((img) => (
                <CommandItem
                  key={img}
                  onSelect={() => {
                    onChange(img)
                    setSearch(img)
                    setOpen(false)
                  }}
                >
                  {img}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function TestsSection({
  testConfig,
  onUpdate,
  onSave,
  preservedStarterCode,
  savePending = false,
  problemId,
  problemSlug,
  approaches,
}: TestsSectionProps) {
  const {
    testType = DEFAULT_TEST_TYPE,
    inputParameters,
    outputType,
    testCases,
    className,
    constructorParameters = [],
    classTestCases = [],
    rawTestCases = [],
  } = testConfig
  const [editMode, setEditMode] = useState<'ui' | 'text'>('ui')
  const [textModeContent, setTextModeContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [schemaCollapsed, setSchemaCollapsed] = useState(false)
  const [casesCollapsed, setCasesCollapsed] = useState(false)
  const { data: availableImages = [] } = trpc.admin.images.list.useQuery()

  // Fetch all templates for TypeSelector dropdowns
  const { data: templateListData } = trpc.admin.testTemplates.list.useQuery()
  const availableTemplates: AvailableTemplate[] = useMemo(
    () =>
      (
        (templateListData?.data as Array<{ slug: string; name: string }>) ?? []
      ).map((t) => ({ slug: t.slug, name: t.name })),
    [templateListData],
  )

  // Build set of template IDs missing Python definitionCode (for warning badges)
  const templatesMissingDefinition: Set<string> = useMemo(() => {
    const missing = new Set<string>()
    const templates =
      (templateListData?.data as
        | Array<{
            id: string
            code: Array<{ language: string; definitionCode: string }>
          }>
        | undefined) ?? []
    for (const t of templates) {
      const pythonCode = t.code?.find((c) => c.language === 'python')
      if (!pythonCode || !pythonCode.definitionCode) {
        missing.add(t.id)
      }
    }
    return missing
  }, [templateListData])
  const [expandedTestCases, setExpandedTestCases] = useState<Set<number>>(
    () => new Set(),
  )

  // Test runner state
  const runMutation = trpc.execution.run.useMutation()
  const [runnerLanguage, setRunnerLanguage] = useState<string>('python')
  const [runnerApproachIndex, setRunnerApproachIndex] = useState<number>(0)
  const [runningTestIndex, setRunningTestIndex] = useState<number | null>(null)
  const [runningAll, setRunningAll] = useState(false)
  const [testRunResults, setTestRunResults] = useState<
    Map<
      number,
      {
        status: 'pass' | 'fail' | 'error'
        message?: string
        actual?: string
        expected?: string
      }
    >
  >(new Map())
  const [fullTestResult, setFullTestResult] = useState<TestRunSummary | null>(
    null,
  )

  // AI Formatting panel state
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiSuccess, setAiSuccess] = useState<string | null>(null)
  const [aiFields, setAiFields] = useState({
    intent: false,
    inputDisplayOverride: false,
    outputDisplayOverride: false,
  })
  const [aiOverrideIndices, setAiOverrideIndices] = useState<Set<number>>(
    new Set(),
  )
  const formatMutation = trpc.admin.questions.formatTestCases.useMutation()

  const handleAiFormat = async () => {
    if (!problemId || !aiPrompt.trim()) return

    setAiLoading(true)
    setAiError(null)
    setAiSuccess(null)

    try {
      const currentTestCases =
        testType === TEST_TYPE.CLASS ? classTestCases : testCases
      const casesForAI = currentTestCases.map((tc, i) => ({
        index: i,
        inputs: 'inputs' in tc ? tc.inputs || {} : {},
        expectedOutput: 'expectedOutput' in tc ? tc.expectedOutput || '' : '',
        inputDisplayOverride: tc.inputDisplayOverride || null,
        outputDisplayOverride: tc.outputDisplayOverride || null,
        intent: tc.intent || null,
        constructorArgs:
          'constructorArgs' in tc && tc.constructorArgs != null
            ? typeof tc.constructorArgs === 'string'
              ? tc.constructorArgs
              : JSON.stringify(tc.constructorArgs)
            : null,
        operations:
          'operations' in tc && Array.isArray(tc.operations)
            ? tc.operations.map((op: Operation) => ({
                method: 'method' in op ? op.method || undefined : undefined,
                args: 'args' in op ? op.args || undefined : undefined,
                expected:
                  'expected' in op ? op.expected || undefined : undefined,
                text: 'text' in op ? op.text || undefined : undefined,
                type: op.type || undefined,
              }))
            : null,
      }))

      // Check if there are any empty fields to fill
      const hasEmptyFields = casesForAI.some(
        (tc) =>
          (aiFields.intent && !tc.intent) ||
          (aiFields.inputDisplayOverride && !tc.inputDisplayOverride) ||
          (aiFields.outputDisplayOverride && !tc.outputDisplayOverride),
      )
      if (!hasEmptyFields && aiOverrideIndices.size === 0) {
        setAiSuccess('All fields already have content — nothing to format.')
        setAiLoading(false)
        return
      }

      const result = await formatMutation.mutateAsync({
        problemId,
        prompt: aiPrompt.trim(),
        testCases: casesForAI,
        testConfig: {
          testType: testType || 'function',
          functionName: testConfig.functionNames?.[0],
          className: testConfig.className,
          inputParameters: (inputParameters || []).map((p) => ({
            name: p.name,
            type: p.type,
            order: p.order,
          })),
        },
        fields: aiFields,
        overrideIndices: Array.from(aiOverrideIndices),
      })

      // Apply results to local state
      let updatedCount = 0
      const updatedTestCases = [...currentTestCases]
      for (const r of result.results) {
        if (r.index < 0 || r.index >= updatedTestCases.length) continue
        const tc = { ...updatedTestCases[r.index] }
        let changed = false

        if (
          aiFields.inputDisplayOverride &&
          r.inputDisplayOverride &&
          (aiOverrideIndices.has(r.index) || !tc.inputDisplayOverride)
        ) {
          tc.inputDisplayOverride = r.inputDisplayOverride
          changed = true
        }
        if (
          aiFields.outputDisplayOverride &&
          r.outputDisplayOverride &&
          (aiOverrideIndices.has(r.index) || !tc.outputDisplayOverride)
        ) {
          tc.outputDisplayOverride = r.outputDisplayOverride
          changed = true
        }
        if (
          aiFields.intent &&
          r.intent &&
          (aiOverrideIndices.has(r.index) || !tc.intent)
        ) {
          tc.intent = r.intent
          changed = true
        }

        if (changed) {
          updatedTestCases[r.index] = tc
          updatedCount++
        }
      }

      if (testType === TEST_TYPE.CLASS) {
        onUpdate({
          ...testConfig,
          classTestCases: updatedTestCases as typeof classTestCases,
        })
      } else {
        onUpdate({
          ...testConfig,
          testCases: updatedTestCases as typeof testCases,
        })
      }

      setAiSuccess(
        `Done — ${updatedCount} test case${updatedCount !== 1 ? 's' : ''} updated.`,
      )
    } catch (err) {
      setAiError(
        err instanceof Error
          ? err.message
          : 'AI formatting failed. Please try again.',
      )
    } finally {
      setAiLoading(false)
    }
  }

  const toggleTestCase = (index: number) => {
    setExpandedTestCases((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const expandAllTestCases = () => {
    const count =
      testType === TEST_TYPE.CLASS ? classTestCases.length : testCases.length
    setExpandedTestCases(new Set(Array.from({ length: count }, (_, i) => i)))
  }

  const collapseAllTestCases = () => {
    setExpandedTestCases(new Set())
  }

  // Clear test run results when configuration changes
  useEffect(() => {
    setTestRunResults(new Map())
  }, [])

  // Keep a ref to the latest testConfig to avoid stale closure issues
  // when saving after rapid state changes (e.g., renaming parameters)
  const testConfigRef = useRef(testConfig)
  testConfigRef.current = testConfig

  // Handle test type change
  const handleTestTypeChange = (newType: TestType) => {
    onUpdate({ ...testConfig, testType: newType })
  }

  const handleSave = async () => {
    if (!onSave) return

    setSaving(true)
    try {
      // Use the ref to get the latest testConfig, avoiding stale closure issues
      // when the user changes parameter names and saves quickly before React re-renders
      await onSave(testConfigRef.current)
      toast.success('Test configuration saved')
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to save test configuration',
      )
    } finally {
      setSaving(false)
    }
  }

  // Shares `saving` with the Save button on purpose: both call `onSave` with
  // this config, and the caller writes whichever response lands last back
  // into the form. Serializing them through one pending flag keeps the
  // regenerate's result from being displaced by an earlier routine save.
  const handleRegenerateStarterCode = async () => {
    if (!onSave) return

    setSaving(true)
    try {
      await onSave(testConfigRef.current, { regenerateStarterCode: true })
      toast.success('Starter code regenerated')
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to regenerate starter code',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleRunSingleTest = async (testCaseIndex: number) => {
    if (!problemSlug || !approaches?.length) {
      toast.error('No problem or approaches configured')
      return
    }

    const approach = approaches[runnerApproachIndex]
    const solution = approach?.solutions?.find(
      (s) => s.language === runnerLanguage,
    )
    if (!solution) {
      toast.error(
        `No ${runnerLanguage} solution found for ${approach?.name || 'this approach'}`,
      )
      return
    }

    setRunningTestIndex(testCaseIndex)

    try {
      const isClassBased = testType === TEST_TYPE.CLASS
      const mutationInput: Record<string, unknown> = {
        code: solution.code,
        problemId: problemSlug,
        language: runnerLanguage,
      }

      if (testType === TEST_TYPE.RAW) {
        const tc = rawTestCases[testCaseIndex]
        const script = tc.scripts?.[runnerLanguage]
        if (!script) {
          toast.error(`No ${runnerLanguage} script for this test case`)
          setRunningTestIndex(null)
          return
        }
        mutationInput.rawTestScripts = [script]
      } else if (isClassBased) {
        const tc = classTestCases[testCaseIndex]
        mutationInput.classTestCases = [
          {
            constructorArgs: tc.constructorArgs || '[]',
            operations: tc.operations
              .filter((op) => !isComment(op))
              .map((op) => ({
                method: (op as OperationItem).method,
                args: (op as OperationItem).args,
                expected: (op as OperationItem).expected,
                expectsError: (op as OperationItem).expectsError,
              })),
          },
        ]
      } else {
        const tc = testCases[testCaseIndex]
        mutationInput.testCases = [
          {
            inputs: tc.inputs,
            expectedOutput: tc.expectedOutput,
          },
        ]
      }

      const data = await runMutation.mutateAsync(
        mutationInput as Parameters<typeof runMutation.mutateAsync>[0],
      )

      const testResult = data.results?.[0]
      if (testResult) {
        setTestRunResults((prev) =>
          new Map(prev).set(testCaseIndex, {
            status: testResult.passed ? 'pass' : 'fail',
            message: testResult.passed
              ? 'Passed'
              : testResult.error ||
                `Expected ${JSON.stringify(testResult.expected)}, got ${JSON.stringify(testResult.actual)}`,
            actual: JSON.stringify(testResult.actual),
            expected: JSON.stringify(testResult.expected),
          }),
        )
      } else if (data.compilationError) {
        setTestRunResults((prev) =>
          new Map(prev).set(testCaseIndex, {
            status: 'error',
            message: `Compilation error: ${data.compilationError}`,
          }),
        )
      } else {
        setTestRunResults((prev) =>
          new Map(prev).set(testCaseIndex, {
            status: 'error',
            message: data.runtimeError || 'Unknown error',
          }),
        )
      }
    } catch (err) {
      setTestRunResults((prev) =>
        new Map(prev).set(testCaseIndex, {
          status: 'error',
          message: err instanceof Error ? err.message : 'Execution failed',
        }),
      )
    } finally {
      setRunningTestIndex(null)
    }
  }

  const handleRunAllTests = async () => {
    if (!problemSlug || !approaches?.length) return

    const approach = approaches[runnerApproachIndex]
    const solution = approach?.solutions?.find(
      (s) => s.language === runnerLanguage,
    )
    if (!solution) {
      toast.error(`No ${runnerLanguage} solution found`)
      return
    }

    setRunningAll(true)
    setTestRunResults(new Map())
    setFullTestResult({ status: 'running' })

    try {
      const isClassBased = testType === TEST_TYPE.CLASS
      const mutationInput: Record<string, unknown> = {
        code: solution.code,
        problemId: problemSlug,
        language: runnerLanguage,
      }

      if (testType === TEST_TYPE.RAW) {
        const scripts = rawTestCases
          .map((tc) => tc.scripts?.[runnerLanguage])
          .filter((s): s is string => !!s)
        if (scripts.length === 0) {
          toast.error(`No ${runnerLanguage} scripts found in test cases`)
          setRunningAll(false)
          return
        }
        mutationInput.rawTestScripts = scripts
      } else if (isClassBased) {
        mutationInput.classTestCases = classTestCases.map((tc) => ({
          constructorArgs: tc.constructorArgs || '[]',
          operations: tc.operations
            .filter((op) => !isComment(op))
            .map((op) => ({
              method: (op as OperationItem).method,
              args: (op as OperationItem).args,
              expected: (op as OperationItem).expected,
              expectsError: (op as OperationItem).expectsError,
            })),
        }))
      } else {
        mutationInput.testCases = testCases.map((tc) => ({
          inputs: tc.inputs,
          expectedOutput: tc.expectedOutput,
        }))
      }

      const data = await runMutation.mutateAsync(
        mutationInput as Parameters<typeof runMutation.mutateAsync>[0],
      )

      const newResults = new Map<
        number,
        {
          status: 'pass' | 'fail' | 'error'
          message?: string
          actual?: string
          expected?: string
        }
      >()
      if (data.results) {
        for (const r of data.results) {
          newResults.set(r.index, {
            status: r.passed ? 'pass' : 'fail',
            message: r.passed
              ? 'Passed'
              : r.error ||
                `Expected ${JSON.stringify(r.expected)}, got ${JSON.stringify(r.actual)}`,
            actual: JSON.stringify(r.actual),
            expected: JSON.stringify(r.expected),
          })
        }
      }
      if (data.compilationError) {
        const total =
          testType === TEST_TYPE.RAW
            ? rawTestCases.length
            : isClassBased
              ? classTestCases.length
              : testCases.length
        for (let i = 0; i < total; i++) {
          newResults.set(i, {
            status: 'error',
            message: `Compilation error: ${data.compilationError}`,
          })
        }
      }

      setTestRunResults(newResults)

      const passed = data.summary?.passed ?? 0
      const total = data.summary?.total ?? 0
      const allPassed = passed === total && total > 0

      setFullTestResult({
        status: allPassed ? 'success' : 'error',
        message: allPassed
          ? `All tests passed! (${passed}/${total})`
          : data.compilationError
            ? `Compilation error: ${data.compilationError}`
            : data.runtimeError
              ? `Runtime error: ${data.runtimeError}`
              : `${passed}/${total} tests passed`,
        passed,
        total,
        testCases: data.results,
        compileOutput: data.compilationError
          ? String(data.compilationError)
          : undefined,
      })

      if (allPassed) {
        toast.success(`All ${total} tests passed!`)
      } else {
        toast.error(`${passed}/${total} tests passed`)
      }
    } catch (err) {
      setFullTestResult({
        status: 'error',
        message: err instanceof Error ? err.message : 'Execution failed',
      })
      toast.error(err instanceof Error ? err.message : 'Execution failed')
    } finally {
      setRunningAll(false)
    }
  }

  // Convert test cases to text format
  const testCasesToText = useCallback(
    (cases: TestCase[]): string => {
      if (inputParameters.length === 0) return ''

      const lines: string[] = []
      for (const testCase of cases) {
        // Add optional intent (if present)
        if (testCase.intent) {
          lines.push(`Intent: ${testCase.intent}`)
        }
        if (testCase.imageFilename)
          lines.push(`Image: ${testCase.imageFilename}`)
        if (testCase.inputDisplayOverride)
          lines.push(
            `InputOverride: ${escapeForTextMode(testCase.inputDisplayOverride)}`,
          )
        if (testCase.outputDisplayOverride)
          lines.push(
            `OutputOverride: ${escapeForTextMode(testCase.outputDisplayOverride)}`,
          )
        // Add input values in order
        for (const param of inputParameters) {
          lines.push(testCase.inputs[param.name] || '')
        }
        // Add expected output (escape for text mode)
        lines.push(escapeForTextMode(testCase.expectedOutput || ''))
      }
      return lines.join('\n')
    },
    [inputParameters],
  )

  // Parse text format to test cases
  const textToTestCases = (text: string): TestCase[] => {
    if (inputParameters.length === 0) return []

    const lines = text.split('\n')
    const cases: TestCase[] = []

    let i = 0
    while (i < lines.length) {
      let intent: string | undefined
      let imageFilename: string | undefined
      let inputDisplayOverride: string | undefined
      let outputDisplayOverride: string | undefined

      // Check for optional Intent: prefix
      if (lines[i]?.startsWith('Intent: ')) {
        intent = lines[i].substring('Intent: '.length)
        i++
      }

      // Check for optional Image: prefix
      if (lines[i]?.startsWith('Image: ')) {
        imageFilename = lines[i].substring('Image: '.length)
        i++
      }

      // Check for optional InputOverride: prefix
      if (lines[i]?.startsWith('InputOverride: ')) {
        inputDisplayOverride = unescapeFromTextMode(
          lines[i].substring('InputOverride: '.length),
        )
        i++
      }

      // Check for optional OutputOverride: prefix
      if (lines[i]?.startsWith('OutputOverride: ')) {
        outputDisplayOverride = unescapeFromTextMode(
          lines[i].substring('OutputOverride: '.length),
        )
        i++
      }

      // Now read the required input parameters
      if (i + inputParameters.length + 1 > lines.length) break // Incomplete test case

      const inputs: Record<string, string> = {}
      for (let j = 0; j < inputParameters.length; j++) {
        inputs[inputParameters[j].name] = lines[i + j] || ''
      }
      i += inputParameters.length

      // Read expected output (unescape from text mode)
      const expectedOutput = unescapeFromTextMode(lines[i] || '')
      i++

      cases.push({
        inputs,
        expectedOutput,
        order: cases.length,
        intent,
        imageFilename,
        inputDisplayOverride,
        outputDisplayOverride,
        // Automatically mark first 3 as examples
        showAsExample: cases.length < 3,
      })
    }

    return cases
  }

  // Convert class test cases to text format
  // Format:
  // Intent: description (optional)
  // [constructorArg1, arg2] (if constructor params exist)
  // methodName [arg1, arg2]
  // expectedValue
  // ---
  const classTestCasesToText = useCallback(
    (cases: ClassTestCase[]): string => {
      const blocks: string[] = []

      for (const testCase of cases) {
        const lines: string[] = []

        // Add optional intent
        if (testCase.intent) {
          lines.push(`Intent: ${testCase.intent}`)
        }

        if (testCase.imageFilename)
          lines.push(`Image: ${testCase.imageFilename}`)
        if (testCase.inputDisplayOverride)
          lines.push(
            `InputOverride: ${escapeForTextMode(testCase.inputDisplayOverride)}`,
          )
        if (testCase.outputDisplayOverride)
          lines.push(
            `OutputOverride: ${escapeForTextMode(testCase.outputDisplayOverride)}`,
          )

        // Add constructor args if defined and constructor has parameters
        if (constructorParameters.length > 0 && testCase.constructorArgs) {
          lines.push(testCase.constructorArgs)
        }

        // Add operations and comments
        for (const op of testCase.operations) {
          if (isComment(op)) {
            // Escape comment text for text mode
            lines.push(`Comment: ${escapeForTextMode(op.text)}`)
          } else {
            lines.push(`${op.method} ${op.args}`)
            // Escape expected value for text mode
            lines.push(escapeForTextMode(op.expected))
          }
        }

        blocks.push(lines.join('\n'))
      }

      return blocks.join('\n---\n')
    },
    [constructorParameters],
  )

  // Parse text format to class test cases
  const textToClassTestCases = (text: string): ClassTestCase[] => {
    if (!text.trim()) return []

    const blocks = text.split('\n---\n')
    const cases: ClassTestCase[] = []

    for (const block of blocks) {
      const lines = block.split('\n').filter((line) => line.trim() !== '')
      if (lines.length === 0) continue

      let i = 0
      let intent: string | undefined
      let imageFilename: string | undefined
      let inputDisplayOverride: string | undefined
      let outputDisplayOverride: string | undefined
      let constructorArgs: string | undefined

      // Check for optional Intent: prefix
      if (lines[i]?.startsWith('Intent: ')) {
        intent = lines[i].substring('Intent: '.length)
        i++
      }

      // Check for optional Image: prefix
      if (lines[i]?.startsWith('Image: ')) {
        imageFilename = lines[i].substring('Image: '.length)
        i++
      }

      // Check for optional InputOverride: prefix
      if (lines[i]?.startsWith('InputOverride: ')) {
        inputDisplayOverride = unescapeFromTextMode(
          lines[i].substring('InputOverride: '.length),
        )
        i++
      }

      // Check for optional OutputOverride: prefix
      if (lines[i]?.startsWith('OutputOverride: ')) {
        outputDisplayOverride = unescapeFromTextMode(
          lines[i].substring('OutputOverride: '.length),
        )
        i++
      }

      // Check for constructor args (line starting with '[')
      if (
        constructorParameters.length > 0 &&
        lines[i]?.trim().startsWith('[')
      ) {
        constructorArgs = lines[i].trim()
        i++
      }

      // Parse operations and comments
      const operations: Operation[] = []
      while (i < lines.length) {
        const currentLine = lines[i]

        // Check for Comment: prefix
        if (currentLine?.startsWith('Comment: ')) {
          const commentText = unescapeFromTextMode(
            currentLine.substring('Comment: '.length),
          )
          operations.push({ type: 'comment', text: commentText })
          i++
          continue
        }

        // Need at least 2 lines for a method call + expected value
        if (i >= lines.length - 1) break

        const methodLine = currentLine
        const expectedLine = lines[i + 1]

        // Parse method line: "methodName [args]" or "methodName []"
        const match = methodLine.match(/^(\w+)\s*(\[.*\])?$/)
        if (match) {
          operations.push({
            type: 'operation',
            method: match[1],
            args: match[2] || '[]',
            // Unescape expected value from text mode
            expected: unescapeFromTextMode(expectedLine || 'null'),
          })
          i += 2
        } else {
          // Skip malformed lines
          i++
        }
      }

      if (operations.length > 0) {
        cases.push({
          intent,
          imageFilename,
          inputDisplayOverride,
          outputDisplayOverride,
          constructorArgs,
          operations,
          order: cases.length,
        })
      }
    }

    return cases
  }

  // Update text mode content when switching to text mode or when input parameters change
  // We intentionally exclude testCases and testCasesToText to avoid re-syncing while user is editing
  useEffect(() => {
    if (editMode === 'text') {
      if (testType === TEST_TYPE.CLASS) {
        setTextModeContent(classTestCasesToText(classTestCases))
      } else {
        setTextModeContent(testCasesToText(testCases))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when mode changes or parameters are added/removed
  }, [
    editMode,
    testType,
    classTestCases,
    classTestCasesToText,
    testCases,
    testCasesToText,
  ])

  const handleUpdateClassTestCase = useCallback(
    (index: number, updates: Partial<ClassTestCase>) => {
      const updated = [...classTestCases]
      const updatedCase = { ...updated[index], ...updates }
      if (
        ('constructorArgs' in updates || 'operations' in updates) &&
        (updatedCase.inputDisplayOverride || updatedCase.outputDisplayOverride)
      ) {
        updatedCase.displayOverrideStale = true
      }
      updated[index] = updatedCase
      onUpdate({ ...testConfig, classTestCases: updated })
    },
    [classTestCases, testConfig, onUpdate],
  )

  // Handle mode toggle
  const handleModeToggle = (newMode: 'ui' | 'text') => {
    if (newMode === 'text') {
      // Switching to text mode - convert current test cases to text
      if (testType === TEST_TYPE.CLASS) {
        setTextModeContent(classTestCasesToText(classTestCases))
      } else {
        setTextModeContent(testCasesToText(testCases))
      }
    } else {
      // Switching to UI mode - parse text and update test cases
      if (testType === TEST_TYPE.CLASS) {
        const parsedCases = textToClassTestCases(textModeContent)
        onUpdate({ ...testConfig, classTestCases: parsedCases })
      } else {
        const parsedCases = textToTestCases(textModeContent)
        onUpdate({ ...testConfig, testCases: parsedCases })
      }
    }
    setEditMode(newMode)
  }

  // Handle text mode content change
  const handleTextChange = (text: string) => {
    setTextModeContent(text)
    // Parse and update test cases in real-time
    if (testType === TEST_TYPE.CLASS) {
      const parsedCases = textToClassTestCases(text)
      onUpdate({ ...testConfig, classTestCases: parsedCases })
    } else {
      const parsedCases = textToTestCases(text)
      onUpdate({ ...testConfig, testCases: parsedCases })
    }
  }

  // Input Parameter Management
  const handleAddParameter = () => {
    const newParam: InputParameter = {
      name: '',
      type: 'integer',
      order: inputParameters.length,
    }
    onUpdate({
      ...testConfig,
      inputParameters: [...inputParameters, newParam],
    })
  }

  const handleUpdateParameter = (
    index: number,
    updates: Partial<InputParameter>,
  ) => {
    const oldName = inputParameters[index].name
    const updated = [...inputParameters]
    updated[index] = { ...updated[index], ...updates }

    // If the parameter name changed, update all test cases to use the new key
    let updatedTestCases = testCases
    if (updates.name && updates.name !== oldName && oldName) {
      updatedTestCases = testCases.map((testCase) => {
        // Rename key in inputs
        const { [oldName]: oldValue, ...restInputs } = testCase.inputs
        const newInputs =
          oldValue !== undefined
            ? { ...restInputs, [updates.name!]: oldValue }
            : restInputs

        // Also rename key in expectedInputsAfter if it exists
        let newExpectedInputsAfter = testCase.expectedInputsAfter
        if (
          testCase.expectedInputsAfter &&
          oldName in testCase.expectedInputsAfter
        ) {
          const { [oldName]: oldAfterValue, ...restAfter } =
            testCase.expectedInputsAfter
          newExpectedInputsAfter =
            oldAfterValue !== undefined
              ? { ...restAfter, [updates.name!]: oldAfterValue }
              : restAfter
        }

        return {
          ...testCase,
          inputs: newInputs,
          expectedInputsAfter: newExpectedInputsAfter,
        }
      })
    }

    onUpdate({
      ...testConfig,
      inputParameters: updated,
      testCases: updatedTestCases,
    })
  }

  const handleRemoveParameter = (index: number) => {
    const paramToRemove = inputParameters[index]
    const updated = inputParameters.filter((_, i) => i !== index)

    // Re-order remaining parameters
    const reordered = updated.map((param, i) => ({ ...param, order: i }))

    // Remove this parameter from all test cases (both inputs and expectedInputsAfter)
    const updatedTestCases = testCases.map((testCase) => {
      const { [paramToRemove.name]: _, ...remainingInputs } = testCase.inputs

      // Also remove from expectedInputsAfter if it exists
      let remainingExpectedInputsAfter = testCase.expectedInputsAfter
      if (
        testCase.expectedInputsAfter &&
        paramToRemove.name in testCase.expectedInputsAfter
      ) {
        const { [paramToRemove.name]: __, ...restAfter } =
          testCase.expectedInputsAfter
        remainingExpectedInputsAfter = restAfter
      }

      return {
        ...testCase,
        inputs: remainingInputs,
        expectedInputsAfter: remainingExpectedInputsAfter,
      }
    })

    onUpdate({
      ...testConfig,
      inputParameters: reordered,
      testCases: updatedTestCases,
    })
  }

  // Add a test-only parameter (userFacing: false)
  const handleAddTestOnlyParameter = () => {
    const newParam: InputParameter = {
      name: '',
      type: 'integer',
      order: inputParameters.length,
      userFacing: false,
    }
    onUpdate({
      ...testConfig,
      inputParameters: [...inputParameters, newParam],
    })
  }

  // Computed: split parameters into user-facing and test-only with stable IDs
  const paramsWithIds = useMemo(
    () =>
      inputParameters.map((p, originalIndex) => ({
        ...p,
        originalIndex,
        stableId: p.id || `param-${originalIndex}`,
      })),
    [inputParameters],
  )
  const userFacingParams = useMemo(
    () => paramsWithIds.filter((p) => p.userFacing !== false),
    [paramsWithIds],
  )
  const testOnlyParams = useMemo(
    () => paramsWithIds.filter((p) => p.userFacing === false),
    [paramsWithIds],
  )
  const userFacingIds = useMemo(
    () => userFacingParams.map((p) => p.stableId),
    [userFacingParams],
  )
  const testOnlyIds = useMemo(
    () => testOnlyParams.map((p) => p.stableId),
    [testOnlyParams],
  )

  // DnD sensors and handlers for parameter reordering + cross-section moves
  const paramDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const findParamContainer = useCallback(
    (id: string): 'user-facing' | 'test-only' | null => {
      if (userFacingIds.includes(id)) return 'user-facing'
      if (testOnlyIds.includes(id)) return 'test-only'
      return null
    },
    [userFacingIds, testOnlyIds],
  )

  const handleParamDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over) return

      const activeId = active.id as string
      const overId = over.id as string

      const sourceContainer = findParamContainer(activeId)
      // Determine target container: if dropped on a container ID, use that; otherwise find which container the over item is in
      const targetContainer: 'user-facing' | 'test-only' | null =
        overId === 'user-facing' || overId === 'test-only'
          ? (overId as 'user-facing' | 'test-only')
          : findParamContainer(overId)

      if (!sourceContainer || !targetContainer) return

      const sourceList =
        sourceContainer === 'user-facing' ? userFacingParams : testOnlyParams
      const targetList =
        targetContainer === 'user-facing' ? userFacingParams : testOnlyParams

      const activeParam = paramsWithIds.find((p) => p.stableId === activeId)
      if (!activeParam) return

      const updated = [...inputParameters]

      if (sourceContainer === targetContainer) {
        // Reorder within same section
        const oldIndex = sourceList.findIndex((p) => p.stableId === activeId)
        const newIndex = sourceList.findIndex((p) => p.stableId === overId)
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

        // Swap in the full array using original indices
        const reordered = arrayMove(
          sourceList.map((p) => p.originalIndex),
          oldIndex,
          newIndex,
        )
        // Build the new full array preserving the other section's order
        const otherList =
          sourceContainer === 'user-facing' ? testOnlyParams : userFacingParams
        const newParams: InputParameter[] = []
        for (const idx of reordered) {
          newParams.push(updated[idx])
        }
        for (const p of otherList) {
          newParams.push(updated[p.originalIndex])
        }
        onUpdate({
          ...testConfig,
          inputParameters: newParams.map((p, i) => ({ ...p, order: i })),
        })
      } else {
        // Cross-container: toggle userFacing and reposition
        const newUserFacing = targetContainer === 'user-facing'
        updated[activeParam.originalIndex] = {
          ...updated[activeParam.originalIndex],
          userFacing: newUserFacing || undefined,
        }

        // Find insertion index in target
        let insertAfterOriginalIndex = -1
        if (overId !== targetContainer) {
          const overParam = targetList.find((p) => p.stableId === overId)
          if (overParam) {
            insertAfterOriginalIndex = overParam.originalIndex
          }
        }

        // Rebuild: remove from source position, insert into target position
        // Build ordered lists from the updated array
        const allWithIdx = updated.map((p, i) => ({ ...p, _idx: i }))
        const newUserFacingList = allWithIdx.filter(
          (p) => p.userFacing !== false,
        )
        const newTestOnlyList = allWithIdx.filter((p) => p.userFacing === false)

        // If we have a specific insertion point, move the item there
        if (insertAfterOriginalIndex >= 0) {
          const list = newUserFacing ? newUserFacingList : newTestOnlyList
          const currentPos = list.findIndex(
            (p) => p._idx === activeParam.originalIndex,
          )
          const targetPos = list.findIndex(
            (p) => p._idx === insertAfterOriginalIndex,
          )
          if (currentPos >= 0 && targetPos >= 0 && currentPos !== targetPos) {
            const [item] = list.splice(currentPos, 1)
            const adjustedTarget =
              targetPos > currentPos ? targetPos - 1 : targetPos
            list.splice(adjustedTarget, 0, item)
          }
        }

        const newParams = [
          ...newUserFacingList.map(({ _idx, ...p }) => p),
          ...newTestOnlyList.map(({ _idx, ...p }) => p),
        ]
        onUpdate({
          ...testConfig,
          inputParameters: newParams.map((p, i) => ({ ...p, order: i })),
        })
      }
    },
    [
      inputParameters,
      paramsWithIds,
      userFacingParams,
      testOnlyParams,
      findParamContainer,
      onUpdate,
      testConfig,
    ],
  )

  // Constructor Parameter Management (for class-based tests)
  const handleAddConstructorParameter = () => {
    const newParam: InputParameter = {
      name: '',
      type: 'integer',
      order: constructorParameters.length,
    }
    onUpdate({
      ...testConfig,
      constructorParameters: [...constructorParameters, newParam],
    })
  }

  const handleUpdateConstructorParameter = (
    index: number,
    updates: Partial<InputParameter>,
  ) => {
    const updated = [...constructorParameters]
    updated[index] = { ...updated[index], ...updates }
    onUpdate({ ...testConfig, constructorParameters: updated })
  }

  const handleRemoveConstructorParameter = (index: number) => {
    const updated = constructorParameters.filter((_, i) => i !== index)
    const reordered = updated.map((param, i) => ({ ...param, order: i }))
    onUpdate({ ...testConfig, constructorParameters: reordered })
  }

  const handleMoveConstructorParameter = (
    index: number,
    direction: 'up' | 'down',
  ) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === constructorParameters.length - 1)
    ) {
      return
    }

    const updated = [...constructorParameters]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate({
      ...testConfig,
      constructorParameters: updated.map((param, i) => ({
        ...param,
        order: i,
      })),
    })
  }

  // Test Case Management
  const handleAddTestCase = () => {
    const newIndex = testCases.length
    const newTestCase: TestCase = {
      inputs: inputParameters.reduce(
        (acc, param) => {
          acc[param.name] = ''
          return acc
        },
        {} as Record<string, string>,
      ),
      expectedOutput: '',
      order: newIndex,
      intent: '',
      // Automatically mark as example if it's one of the first 3
      showAsExample: newIndex < 3,
    }
    onUpdate({ ...testConfig, testCases: [...testCases, newTestCase] })
  }

  const handleUpdateTestCase = (index: number, updates: Partial<TestCase>) => {
    const updated = [...testCases]
    const updatedCase = { ...updated[index], ...updates }
    // Detect staleness when inputs or expectedOutput change with display overrides set
    if (
      ('inputs' in updates || 'expectedOutput' in updates) &&
      (updatedCase.inputDisplayOverride || updatedCase.outputDisplayOverride)
    ) {
      updatedCase.displayOverrideStale = true
    }
    updated[index] = updatedCase
    onUpdate({ ...testConfig, testCases: updated })
  }

  const handleRemoveTestCase = (index: number) => {
    const updated = testCases.filter((_, i) => i !== index)
    // Re-order remaining test cases, preserving showAsExample values
    const reordered = updated.map((tc, i) => ({ ...tc, order: i }))
    onUpdate({
      ...testConfig,
      testCases: reordered,
    })
  }

  const handleMoveTestCase = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === testCases.length - 1)
    ) {
      return
    }

    const updated = [...testCases]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    // Update order values, preserving showAsExample values
    const reordered = updated.map((tc, i) => ({ ...tc, order: i }))
    onUpdate({
      ...testConfig,
      testCases: reordered,
    })
  }

  // Class Test Case Management
  const handleAddClassTestCase = () => {
    const newIndex = classTestCases.length
    const newTestCase: ClassTestCase = {
      constructorArgs: constructorParameters.length > 0 ? '[]' : undefined,
      operations: [{ method: '', args: '[]', expected: 'null' }],
      order: newIndex,
      intent: '',
      // Automatically mark as example if it's one of the first 3
      showAsExample: newIndex < 3,
    }
    onUpdate({
      ...testConfig,
      classTestCases: [...classTestCases, newTestCase],
    })
  }

  const handleRemoveClassTestCase = (index: number) => {
    const updated = classTestCases.filter((_, i) => i !== index)
    onUpdate({
      ...testConfig,
      classTestCases: updated.map((tc, i) => ({ ...tc, order: i })),
    })
  }

  const handleMoveClassTestCase = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === classTestCases.length - 1)
    ) {
      return
    }

    const updated = [...classTestCases]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate({
      ...testConfig,
      classTestCases: updated.map((tc, i) => ({ ...tc, order: i })),
    })
  }

  // Raw Test Case Management
  const handleAddRawTestCase = () => {
    const newIndex = rawTestCases.length
    const newTestCase: RawTestCase = {
      scripts: { python: '' },
      exampleDisplay: '',
      order: newIndex,
      intent: '',
      showAsExample: newIndex < 3,
    }
    onUpdate({
      ...testConfig,
      rawTestCases: [...rawTestCases, newTestCase],
    })
  }

  const handleRemoveRawTestCase = (index: number) => {
    const updated = rawTestCases.filter((_, i) => i !== index)
    onUpdate({
      ...testConfig,
      rawTestCases: updated.map((tc, i) => ({ ...tc, order: i })),
    })
  }

  const handleUpdateRawTestCase = (
    index: number,
    updates: Partial<RawTestCase>,
  ) => {
    const updated = [...rawTestCases]
    updated[index] = { ...updated[index], ...updates }
    onUpdate({ ...testConfig, rawTestCases: updated })
  }

  // Operation Management within a class test case
  const handleAddOperation = (testCaseIndex: number) => {
    const updated = [...classTestCases]
    updated[testCaseIndex] = {
      ...updated[testCaseIndex],
      operations: [
        ...updated[testCaseIndex].operations,
        { type: 'operation', method: '', args: '[]', expected: 'null' },
      ],
    }
    onUpdate({ ...testConfig, classTestCases: updated })
  }

  // Add a comment to a test case
  const handleAddComment = (testCaseIndex: number) => {
    const updated = [...classTestCases]
    updated[testCaseIndex] = {
      ...updated[testCaseIndex],
      operations: [
        ...updated[testCaseIndex].operations,
        { type: 'comment', text: '' },
      ],
    }
    onUpdate({ ...testConfig, classTestCases: updated })
  }

  const handleUpdateOperation = (
    testCaseIndex: number,
    opIndex: number,
    updates: Partial<Operation>,
  ) => {
    const updated = [...classTestCases]
    const operations = [...updated[testCaseIndex].operations]
    operations[opIndex] = { ...operations[opIndex], ...updates } as Operation
    updated[testCaseIndex] = { ...updated[testCaseIndex], operations }
    onUpdate({ ...testConfig, classTestCases: updated })
  }

  const handleRemoveOperation = (testCaseIndex: number, opIndex: number) => {
    const updated = [...classTestCases]
    updated[testCaseIndex] = {
      ...updated[testCaseIndex],
      operations: updated[testCaseIndex].operations.filter(
        (_, i) => i !== opIndex,
      ),
    }
    onUpdate({ ...testConfig, classTestCases: updated })
  }

  const handleMoveOperation = (
    testCaseIndex: number,
    opIndex: number,
    direction: 'up' | 'down',
  ) => {
    const operations = classTestCases[testCaseIndex].operations
    if (
      (direction === 'up' && opIndex === 0) ||
      (direction === 'down' && opIndex === operations.length - 1)
    ) {
      return
    }

    const updated = [...classTestCases]
    const newOps = [...operations]
    const swapIndex = direction === 'up' ? opIndex - 1 : opIndex + 1
    ;[newOps[opIndex], newOps[swapIndex]] = [newOps[swapIndex], newOps[opIndex]]
    updated[testCaseIndex] = { ...updated[testCaseIndex], operations: newOps }
    onUpdate({ ...testConfig, classTestCases: updated })
  }

  return (
    <div className="space-y-8">
      {/* Save Button */}
      {onSave && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || savePending}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Test Configuration
              </>
            )}
          </Button>
        </div>
      )}

      {/* Test Schema Configuration */}
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between p-4">
          <button
            type="button"
            onClick={() => setSchemaCollapsed(!schemaCollapsed)}
            className="-m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
          >
            {schemaCollapsed ? (
              <ChevronRight size={20} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={20} className="text-muted-foreground" />
            )}
            <div className="text-left">
              <h2 className="text-lg font-semibold">Test Schema</h2>
              <p className="text-sm text-muted-foreground">
                {testType === TEST_TYPE.RAW
                  ? 'Raw · admin-authored scripts'
                  : testType === TEST_TYPE.CLASS
                    ? `Class-based · ${className || 'unnamed class'}`
                    : `Function-based · ${userFacingParams.length} param${userFacingParams.length !== 1 ? 's' : ''} · ${outputType} output`}
              </p>
            </div>
          </button>
        </div>
        {!schemaCollapsed && (
          <div className="border-t border-border p-4 space-y-4">
            {/* Test Type + Starter Code (side-by-side layout) */}
            <div className="flex gap-6">
              {/* Left: Test Type + Class Name */}
              <div className="space-y-4">
                {/* Test Type Selector */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Test Type</h3>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={
                        testType === TEST_TYPE.FUNCTION ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => handleTestTypeChange(TEST_TYPE.FUNCTION)}
                      className="flex items-center gap-2"
                    >
                      <FunctionSquare className="h-4 w-4" />
                      Function-Based
                    </Button>
                    <Button
                      type="button"
                      variant={
                        testType === TEST_TYPE.CLASS ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => handleTestTypeChange(TEST_TYPE.CLASS)}
                      className="flex items-center gap-2"
                    >
                      <Box className="h-4 w-4" />
                      Class-Based
                    </Button>
                    <Button
                      type="button"
                      variant={
                        testType === TEST_TYPE.RAW ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => handleTestTypeChange(TEST_TYPE.RAW)}
                      className="flex items-center gap-2"
                    >
                      <Code2 className="h-4 w-4" />
                      Raw
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {testType === TEST_TYPE.FUNCTION
                      ? 'Standard function with inputs and expected output.'
                      : testType === TEST_TYPE.CLASS
                        ? 'Class with constructor and method calls sequence.'
                        : 'Admin-authored test scripts. Student code is prepended to each script.'}
                  </p>
                </div>

                {/* Class Name (only for class-based) */}
                {testType === TEST_TYPE.CLASS && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Class Name</h3>
                    <Input
                      type="text"
                      value={className || ''}
                      onChange={(e) =>
                        onUpdate({ ...testConfig, className: e.target.value })
                      }
                      placeholder="e.g., DynamicArray, HashMap, LRUCache"
                      className="max-w-xs text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Right: Starter Code Widget */}
              {testType !== TEST_TYPE.RAW && (
                <StarterCodeWidget
                  starterCode={testConfig.starterCode}
                  preservedLanguages={preservedStarterCode}
                  onRegenerate={
                    onSave ? handleRegenerateStarterCode : undefined
                  }
                  busy={saving || savePending}
                />
              )}
            </div>

            {/* Constructor Parameters (class-based tests only) */}
            {testType === TEST_TYPE.CLASS && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">
                    Constructor Parameters
                  </h3>
                  <Button
                    type="button"
                    onClick={handleAddConstructorParameter}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Parameter
                  </Button>
                </div>

                {constructorParameters.length === 0 ? (
                  <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-8">
                    <p className="text-muted-foreground mb-3 text-sm">
                      No constructor parameters defined (default constructor).
                    </p>
                    <Button
                      type="button"
                      onClick={handleAddConstructorParameter}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Constructor Parameter
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {constructorParameters.map((param, index) => (
                      <div
                        key={param.id || `param-${index}`}
                        className="border-border bg-muted/30 flex items-center gap-3 rounded-lg border p-3"
                      >
                        <GripVertical className="text-muted-foreground h-4 w-4 flex-shrink-0" />

                        <div className="flex flex-1 gap-3">
                          <div className="flex-1">
                            <Input
                              type="text"
                              value={param.name}
                              onChange={(e) =>
                                handleUpdateConstructorParameter(index, {
                                  name: e.target.value,
                                })
                              }
                              placeholder="Parameter name (e.g., capacity)"
                              className="w-full text-sm"
                            />
                          </div>
                          <div className="w-40">
                            <TypeSelector
                              value={param.type}
                              onChange={(newType) => {
                                if (validateTemplateType(newType, testConfig)) {
                                  return
                                }
                                handleUpdateConstructorParameter(index, {
                                  type: newType,
                                })
                              }}
                              availableTemplates={availableTemplates}
                              className="text-sm"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleMoveConstructorParameter(index, 'up')
                            }
                            disabled={index === 0}
                            className="h-7 w-7 p-0"
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleMoveConstructorParameter(index, 'down')
                            }
                            disabled={
                              index === constructorParameters.length - 1
                            }
                            className="h-7 w-7 p-0"
                          >
                            ↓
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveConstructorParameter(index)
                            }
                            className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Method Signatures (class-based tests only) */}
            {testType === TEST_TYPE.CLASS && (
              <MethodSignaturesEditor
                methods={testConfig.methods || []}
                onChange={(methods) => onUpdate({ ...testConfig, methods })}
              />
            )}

            {/* Input Parameters + Test Infrastructure (function-based tests) */}
            {testType === TEST_TYPE.FUNCTION && (
              <DndContext
                sensors={paramDndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleParamDragEnd}
              >
                {/* User-facing parameters */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium">Input Parameters</h3>
                      <p className="text-muted-foreground text-xs">
                        Parameters the user&apos;s function will receive
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleAddParameter}
                      size="sm"
                      variant="outline"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Parameter
                    </Button>
                  </div>

                  <SortableContext
                    items={userFacingIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableSection id="user-facing">
                      {userFacingParams.length === 0 ? (
                        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-6">
                          <p className="text-muted-foreground mb-3 text-sm">
                            No input parameters defined yet.
                          </p>
                          <Button
                            type="button"
                            onClick={handleAddParameter}
                            variant="outline"
                            size="sm"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add First Parameter
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {userFacingParams.map((param) => (
                            <ParameterRow
                              key={param.stableId}
                              id={param.stableId}
                              param={param}
                              testConfig={testConfig}
                              availableTemplates={availableTemplates}
                              mode="user-facing"
                              onUpdate={handleUpdateParameter}
                              onRemove={handleRemoveParameter}
                            />
                          ))}
                        </div>
                      )}
                    </DroppableSection>
                  </SortableContext>
                </div>

                {/* Test infrastructure parameters */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium">
                        Test Infrastructure Parameters
                      </h3>
                      <p className="text-muted-foreground text-xs">
                        Used by custom_setup to construct data structures (not
                        in starter code)
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleAddTestOnlyParameter}
                      size="sm"
                      variant="outline"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Test-Only Param
                    </Button>
                  </div>

                  <SortableContext
                    items={testOnlyIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableSection id="test-only">
                      {testOnlyParams.length === 0 ? (
                        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-4">
                          <p className="text-muted-foreground text-xs">
                            No test infrastructure parameters. Drag parameters
                            here for problems where custom_setup builds complex
                            data structures.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {testOnlyParams.map((param) => (
                            <ParameterRow
                              key={param.stableId}
                              id={param.stableId}
                              param={param}
                              testConfig={testConfig}
                              availableTemplates={availableTemplates}
                              mode="test-only"
                              onUpdate={handleUpdateParameter}
                              onRemove={handleRemoveParameter}
                            />
                          ))}
                        </div>
                      )}
                    </DroppableSection>
                  </SortableContext>
                </div>
              </DndContext>
            )}

            {/* Output Type (only for function-based) */}
            {testType === TEST_TYPE.FUNCTION && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Output Type</h3>
                <div className="flex items-center gap-3">
                  <TypeSelector
                    value={outputType}
                    onChange={(newType) => {
                      if (validateTemplateType(newType, testConfig)) {
                        return
                      }
                      onUpdate({
                        ...testConfig,
                        outputType: newType,
                      })
                    }}
                    availableTemplates={availableTemplates}
                    className="text-sm"
                  />
                </div>
              </div>
            )}

            {/* Acceptable Function Names (only for function-based) */}
            {testType === TEST_TYPE.FUNCTION && (
              <FunctionNamesEditor
                functionNames={testConfig.functionNames || []}
                onChange={(names) =>
                  onUpdate({ ...testConfig, functionNames: names })
                }
              />
            )}

            {/* Comparison Mode & Flags */}
            {testType !== TEST_TYPE.RAW && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Comparison Mode</h3>
                <div className="flex items-center gap-4">
                  <SimpleSelect
                    value={testConfig.comparisonMode || 'exact'}
                    onChange={(newValue) => {
                      onUpdate({
                        ...testConfig,
                        comparisonMode: newValue as ComparisonMode,
                      })
                    }}
                    options={[
                      { value: 'exact', label: 'Exact Match' },
                      {
                        value: 'set_equality',
                        label: "Set Equality (order doesn't matter)",
                      },
                      { value: 'sorted_equality', label: 'Sorted Equality' },
                    ]}
                    width="lg"
                    className="text-sm"
                  />
                  <label className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={testConfig.caseInsensitive || false}
                      onChange={(e) =>
                        onUpdate({
                          ...testConfig,
                          caseInsensitive: e.target.checked,
                        })
                      }
                      className="h-3.5 w-3.5 rounded border-gray-300"
                    />
                    <span className="text-muted-foreground whitespace-nowrap">
                      Case insensitive
                    </span>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={testConfig.approximate || false}
                      onChange={(e) =>
                        onUpdate({
                          ...testConfig,
                          approximate: e.target.checked,
                          ...(!e.target.checked && { tolerance: undefined }),
                        })
                      }
                      className="h-3.5 w-3.5 rounded border-gray-300"
                    />
                    <span className="text-muted-foreground whitespace-nowrap">
                      Approximate floats
                    </span>
                  </label>
                  {testConfig.approximate && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Tolerance:
                      </span>
                      <Input
                        type="number"
                        step="0.0001"
                        value={testConfig.tolerance ?? 0.0001}
                        onChange={(e) =>
                          onUpdate({
                            ...testConfig,
                            tolerance:
                              Number.parseFloat(e.target.value) || 0.0001,
                          })
                        }
                        className="w-24 text-sm font-mono"
                      />
                    </div>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {testConfig.comparisonMode === 'set_equality'
                    ? "Results are compared as sets - order of elements doesn't matter."
                    : testConfig.comparisonMode === 'sorted_equality'
                      ? 'Both actual and expected are sorted before comparison.'
                      : 'Output must match the expected value exactly.'}
                </p>
              </div>
            )}

            {/* Custom Test Logic Section */}
            {testType !== TEST_TYPE.RAW && (
              <CustomTestLogicSection
                templates={(testConfig.templates || []) as SelectedTemplate[]}
                customCode={
                  (testConfig.customCode || {}) as Record<
                    string,
                    CustomCodeData
                  >
                }
                onTemplatesChange={(templates) =>
                  onUpdate({ ...testConfig, templates })
                }
                onCustomCodeChange={(customCode) =>
                  onUpdate({ ...testConfig, customCode })
                }
                testType={testType}
                templatesMissingDefinition={templatesMissingDefinition}
                isTemplateTypeInUse={[
                  outputType,
                  ...inputParameters.map((p) => p.type),
                  ...constructorParameters.map((p) => p.type),
                ].some((t) => {
                  try {
                    return getReferencedTemplates(parseType(t)).length > 0
                  } catch {
                    return false
                  }
                })}
              />
            )}
          </div>
        )}
      </div>

      {/* Test Cases */}
      <div className="rounded-lg border border-border">
        <div className="flex items-center justify-between p-4">
          <button
            type="button"
            onClick={() => setCasesCollapsed(!casesCollapsed)}
            className="-m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
          >
            {casesCollapsed ? (
              <ChevronRight size={20} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={20} className="text-muted-foreground" />
            )}
            <div className="text-left">
              <h2 className="text-lg font-semibold">Test Cases</h2>
              <p className="text-sm text-muted-foreground">
                {testType === TEST_TYPE.RAW
                  ? `${rawTestCases.length} test case${rawTestCases.length !== 1 ? 's' : ''} \u00b7 ${rawTestCases.filter((tc) => tc.showAsExample).length} example${rawTestCases.filter((tc) => tc.showAsExample).length !== 1 ? 's' : ''}`
                  : testType === TEST_TYPE.CLASS
                    ? `${classTestCases.length} test case${classTestCases.length !== 1 ? 's' : ''} \u00b7 ${classTestCases.filter((tc) => tc.showAsExample).length} example${classTestCases.filter((tc) => tc.showAsExample).length !== 1 ? 's' : ''}`
                    : `${testCases.length} test case${testCases.length !== 1 ? 's' : ''} \u00b7 ${testCases.filter((tc) => tc.showAsExample).length} example${testCases.filter((tc) => tc.showAsExample).length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            {editMode === 'ui' && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={expandAllTestCases}
                  className="text-xs"
                >
                  Expand All
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={collapseAllTestCases}
                  className="text-xs"
                >
                  Collapse All
                </Button>
              </>
            )}
            {/* Mode toggle buttons */}
            {testType !== TEST_TYPE.RAW && (
              <div className="border-border flex rounded-md border">
                <Button
                  type="button"
                  variant={editMode === 'ui' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleModeToggle('ui')}
                  className="rounded-r-none"
                >
                  <List className="mr-2 h-4 w-4" />
                  UI Mode
                </Button>
                <Button
                  type="button"
                  variant={editMode === 'text' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleModeToggle('text')}
                  className="rounded-l-none"
                  disabled={
                    testType === TEST_TYPE.FUNCTION &&
                    inputParameters.length === 0
                  }
                >
                  <Code2 className="mr-2 h-4 w-4" />
                  Text Mode
                </Button>
              </div>
            )}
            {editMode === 'ui' && (
              <Button
                type="button"
                onClick={
                  testType === TEST_TYPE.RAW
                    ? handleAddRawTestCase
                    : testType === TEST_TYPE.CLASS
                      ? handleAddClassTestCase
                      : handleAddTestCase
                }
                size="sm"
                disabled={
                  testType === TEST_TYPE.FUNCTION &&
                  inputParameters.length === 0
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Test Case
              </Button>
            )}
          </div>
        </div>
        {!casesCollapsed && (
          <div className="border-t border-border p-4 space-y-4">
            {/* AI Formatting Panel */}
            {problemId && testType !== TEST_TYPE.RAW && (
              <div className="rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => setAiPanelOpen(!aiPanelOpen)}
                  className="flex w-full items-center gap-2 rounded-lg p-3 text-left transition-colors hover:bg-muted/50"
                >
                  {aiPanelOpen ? (
                    <ChevronDown size={16} className="text-muted-foreground" />
                  ) : (
                    <ChevronRight size={16} className="text-muted-foreground" />
                  )}
                  <Sparkles size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium">AI Formatting</span>
                </button>
                {aiPanelOpen && (
                  <div className="space-y-3 border-t border-border p-3">
                    <Textarea
                      value={aiPrompt}
                      onChange={(e) => {
                        setAiPrompt(e.target.value)
                        setAiError(null)
                        setAiSuccess(null)
                      }}
                      placeholder="e.g., Format tree inputs as ASCII art diagrams"
                      className="min-h-[60px] w-full resize-y text-sm"
                      disabled={aiLoading}
                    />
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFields.intent}
                          onChange={(e) =>
                            setAiFields((f) => ({
                              ...f,
                              intent: e.target.checked,
                            }))
                          }
                          disabled={aiLoading}
                        />
                        Intent
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFields.inputDisplayOverride}
                          onChange={(e) =>
                            setAiFields((f) => ({
                              ...f,
                              inputDisplayOverride: e.target.checked,
                            }))
                          }
                          disabled={aiLoading}
                        />
                        Input Override
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={aiFields.outputDisplayOverride}
                          onChange={(e) =>
                            setAiFields((f) => ({
                              ...f,
                              outputDisplayOverride: e.target.checked,
                            }))
                          }
                          disabled={aiLoading}
                        />
                        Output Override
                      </label>
                    </div>
                    {(aiFields.intent ||
                      aiFields.inputDisplayOverride ||
                      aiFields.outputDisplayOverride) &&
                      (testType === TEST_TYPE.CLASS
                        ? classTestCases
                        : testCases
                      ).length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">
                              Override specific test cases:
                            </span>
                            <button
                              type="button"
                              className="text-xs text-muted-foreground underline hover:text-foreground"
                              onClick={() => {
                                const count = (
                                  testType === TEST_TYPE.CLASS
                                    ? classTestCases
                                    : testCases
                                ).length
                                if (aiOverrideIndices.size === count) {
                                  setAiOverrideIndices(new Set())
                                } else {
                                  setAiOverrideIndices(
                                    new Set(
                                      Array.from(
                                        { length: count },
                                        (_, i) => i,
                                      ),
                                    ),
                                  )
                                }
                              }}
                              disabled={aiLoading}
                            >
                              {aiOverrideIndices.size ===
                              (testType === TEST_TYPE.CLASS
                                ? classTestCases
                                : testCases
                              ).length
                                ? 'Deselect All'
                                : 'Select All'}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Array.from(
                              {
                                length: (testType === TEST_TYPE.CLASS
                                  ? classTestCases
                                  : testCases
                                ).length,
                              },
                              (_, i) => (
                                <label
                                  // biome-ignore lint/suspicious/noArrayIndexKey: static list of sequential checkboxes
                                  key={`override-${i}`}
                                  className="flex items-center gap-1.5 text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={aiOverrideIndices.has(i)}
                                    onChange={(e) => {
                                      setAiOverrideIndices((prev) => {
                                        const next = new Set(prev)
                                        if (e.target.checked) {
                                          next.add(i)
                                        } else {
                                          next.delete(i)
                                        }
                                        return next
                                      })
                                    }}
                                    disabled={aiLoading}
                                  />
                                  {i + 1}
                                </label>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAiFormat}
                        disabled={
                          aiLoading ||
                          !aiPrompt.trim() ||
                          (!aiFields.intent &&
                            !aiFields.inputDisplayOverride &&
                            !aiFields.outputDisplayOverride) ||
                          (testType === TEST_TYPE.CLASS
                            ? classTestCases.length === 0
                            : testCases.length === 0)
                        }
                      >
                        {aiLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Formatting...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Format
                          </>
                        )}
                      </Button>
                      {aiError && (
                        <p className="text-sm text-destructive">{aiError}</p>
                      )}
                      {aiSuccess && (
                        <p className="text-sm text-emerald-600">{aiSuccess}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Test Runner Toolbar */}
            {problemSlug && approaches && approaches.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/20 p-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium">Test Runner</span>
                  <SimpleSelect
                    value={runnerLanguage}
                    onChange={setRunnerLanguage}
                    options={[{ value: 'python', label: 'Python' }]}
                    width="sm"
                    className="text-sm"
                  />
                  <SimpleSelect
                    value={String(runnerApproachIndex)}
                    onChange={(v) => setRunnerApproachIndex(Number(v))}
                    options={(approaches || []).map((a, i) => ({
                      value: String(i),
                      label: a.name || `Approach ${i + 1}`,
                    }))}
                    width="md"
                    className="text-sm"
                  />
                  {(() => {
                    const approach = approaches?.[runnerApproachIndex]
                    const solution = approach?.solutions?.find(
                      (s) => s.language === runnerLanguage,
                    )
                    return solution ? (
                      <span className="text-xs text-muted-foreground">
                        {solution.code.split('\n').length} lines
                      </span>
                    ) : (
                      <span className="text-xs text-destructive">
                        No {runnerLanguage} solution for this approach
                      </span>
                    )
                  })()}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRunAllTests}
                    disabled={runningAll || runningTestIndex !== null}
                  >
                    {runningAll ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="mr-1 h-3 w-3" />
                        Run All
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Test Results (from Run All) */}
            {fullTestResult && fullTestResult.status !== 'pending' && (
              <div className="rounded-lg border border-border overflow-hidden">
                {fullTestResult.status === 'running' ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running tests...
                  </div>
                ) : (
                  <TestResultsSection testResult={fullTestResult} />
                )}
              </div>
            )}

            {/* Raw Test Cases */}
            {testType === TEST_TYPE.RAW ? (
              rawTestCases.length === 0 ? (
                <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                  <p className="text-muted-foreground mb-4">
                    No raw test cases added yet.
                  </p>
                  <Button
                    type="button"
                    onClick={handleAddRawTestCase}
                    variant="outline"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add First Test Case
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {rawTestCases.map((rawTestCase, testCaseIndex) => {
                    const isExpanded = expandedTestCases.has(testCaseIndex)
                    return (
                      <div
                        key={rawTestCase.id || `raw-test-${testCaseIndex}`}
                        className={cn(
                          'border-border rounded-lg border',
                          rawTestCase.showAsExample &&
                            'border-l-4 border-l-blue-500',
                        )}
                      >
                        {/* Header */}
                        <div className="flex items-center gap-3 p-3">
                          <button
                            type="button"
                            onClick={() => toggleTestCase(testCaseIndex)}
                            className="flex flex-1 items-center gap-2 text-left"
                          >
                            {isExpanded ? (
                              <ChevronDown
                                size={16}
                                className="text-muted-foreground"
                              />
                            ) : (
                              <ChevronRight
                                size={16}
                                className="text-muted-foreground"
                              />
                            )}
                            <span className="text-sm font-medium">
                              Test {testCaseIndex + 1}
                            </span>
                            {rawTestCase.showAsExample ? (
                              <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600">
                                EXAMPLE
                              </span>
                            ) : (
                              <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-600">
                                HIDDEN
                              </span>
                            )}
                            {rawTestCase.intent && (
                              <span className="text-muted-foreground text-xs">
                                — {rawTestCase.intent}
                              </span>
                            )}
                          </button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveRawTestCase(testCaseIndex)
                            }
                            className="text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="border-border space-y-4 border-t p-4">
                            {/* Show as Example checkbox */}
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={rawTestCase.showAsExample ?? false}
                                onChange={(e) =>
                                  handleUpdateRawTestCase(testCaseIndex, {
                                    showAsExample: e.target.checked,
                                  })
                                }
                                className="rounded"
                              />
                              Show as Example
                            </label>

                            {/* Example Display (only when showAsExample) */}
                            {rawTestCase.showAsExample && (
                              <div className="space-y-1">
                                <label className="text-sm font-medium">
                                  Example Display
                                </label>
                                <p className="text-muted-foreground text-xs">
                                  Formatted text shown in the problem
                                  description example card
                                </p>
                                <Textarea
                                  value={rawTestCase.exampleDisplay ?? ''}
                                  onChange={(e) =>
                                    handleUpdateRawTestCase(testCaseIndex, {
                                      exampleDisplay: e.target.value,
                                    })
                                  }
                                  placeholder="Enter formatted example text..."
                                  className="min-h-[80px] font-mono text-sm"
                                />
                              </div>
                            )}

                            {/* Intent */}
                            <div className="space-y-1">
                              <label className="text-sm font-medium">
                                Intent
                              </label>
                              <Input
                                value={rawTestCase.intent ?? ''}
                                onChange={(e) =>
                                  handleUpdateRawTestCase(testCaseIndex, {
                                    intent: e.target.value,
                                  })
                                }
                                placeholder="What does this test verify?"
                                className="text-sm"
                              />
                            </div>

                            {/* Test Script (tabbed Monaco editor) */}
                            <RawTestScriptEditor
                              scripts={rawTestCase.scripts || {}}
                              onChange={(scripts) =>
                                handleUpdateRawTestCase(testCaseIndex, {
                                  scripts,
                                })
                              }
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            ) : /* Class-Based Test Cases */
            testType === TEST_TYPE.CLASS ? (
              editMode === 'text' ? (
                /* Class Text Mode */
                <div className="space-y-3">
                  <div className="border-border bg-muted/30 rounded-lg border p-4">
                    <div className="mb-2">
                      <p className="text-muted-foreground text-xs">
                        <strong>Format:</strong> Test cases separated by{' '}
                        <code className="bg-muted rounded px-1">---</code>. Each
                        test case starts with optional{' '}
                        <code className="bg-muted rounded px-1">
                          Intent: ...
                        </code>
                        {constructorParameters.length > 0 && (
                          <>
                            , then constructor args as JSON array (e.g.,{' '}
                            <code className="bg-muted rounded px-1">[2]</code>)
                          </>
                        )}
                        , followed by pairs of method calls and expected values.
                      </p>
                    </div>
                    <div className="relative">
                      <Textarea
                        value={textModeContent}
                        onChange={(e) => handleTextChange(e.target.value)}
                        placeholder={`Intent: Test basic operations${constructorParameters.length > 0 ? '\n[2]' : ''}
append [0]
null
get [0]
0
---
Intent: Another test case
${constructorParameters.length > 0 ? '[5]\n' : ''}put [1, "value"]
null`}
                        className="min-h-[300px] w-full resize-y font-mono text-sm"
                        style={{
                          lineHeight: '1.5',
                          tabSize: 2,
                        }}
                      />
                    </div>
                    <div className="text-muted-foreground mt-2 text-xs">
                      {classTestCases.length} test case
                      {classTestCases.length !== 1 ? 's' : ''} detected
                    </div>
                  </div>
                </div>
              ) : classTestCases.length === 0 ? (
                <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                  <p className="text-muted-foreground mb-4">
                    No test cases added yet.
                  </p>
                  <Button
                    type="button"
                    onClick={handleAddClassTestCase}
                    variant="outline"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add First Test Case
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {classTestCases.map((classTestCase, testCaseIndex) => {
                    const isExpanded = expandedTestCases.has(testCaseIndex)
                    const firstOp = classTestCase.operations.find(
                      (op) => op.type !== 'comment',
                    )
                    const preview = classTestCase.intent
                      ? classTestCase.intent
                      : firstOp && 'method' in firstOp
                        ? `${firstOp.method}(...) ${classTestCase.operations.filter((op) => op.type !== 'comment').length} ops`
                        : `${classTestCase.operations.filter((op) => op.type !== 'comment').length} operations`

                    return (
                      <div
                        key={classTestCase.id || `class-test-${testCaseIndex}`}
                        className={cn(
                          'border-border rounded-lg border',
                          classTestCase.showAsExample &&
                            'border-l-4 border-l-blue-500',
                        )}
                      >
                        {/* Always-visible header */}
                        <div
                          className="flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => toggleTestCase(testCaseIndex)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium flex-shrink-0">
                            #{testCaseIndex + 1}
                          </span>
                          {classTestCase.showAsExample && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex-shrink-0">
                              Example
                            </span>
                          )}
                          {/* Preview when collapsed */}
                          {!isExpanded && (
                            <span className="text-muted-foreground text-xs font-mono truncate">
                              {preview}
                            </span>
                          )}
                          {/* Action buttons */}
                          <div
                            className="ml-auto flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Result indicator */}
                            {testRunResults.get(testCaseIndex) && (
                              <span
                                className={cn(
                                  'rounded px-1.5 py-0.5 text-xs font-medium',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'pass' &&
                                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'fail' &&
                                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'error' &&
                                    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                                )}
                              >
                                {testRunResults.get(testCaseIndex)!.status ===
                                'pass'
                                  ? 'Pass'
                                  : testRunResults.get(testCaseIndex)!
                                        .status === 'fail'
                                    ? 'Fail'
                                    : 'Err'}
                              </span>
                            )}
                            {/* Play button */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRunSingleTest(testCaseIndex)}
                              disabled={runningTestIndex !== null || runningAll}
                              className="h-6 w-6 p-0"
                              title="Run this test case"
                            >
                              {runningTestIndex === testCaseIndex ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={classTestCase.showAsExample || false}
                                onChange={(e) =>
                                  handleUpdateClassTestCase(testCaseIndex, {
                                    showAsExample: e.target.checked,
                                  })
                                }
                                className="h-3 w-3 rounded border-gray-300"
                              />
                              <span className="text-muted-foreground text-xs">
                                Ex
                              </span>
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleMoveClassTestCase(testCaseIndex, 'up')
                              }
                              disabled={testCaseIndex === 0}
                              className="h-7 w-7 p-0"
                            >
                              ↑
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleMoveClassTestCase(testCaseIndex, 'down')
                              }
                              disabled={
                                testCaseIndex === classTestCases.length - 1
                              }
                              className="h-7 w-7 p-0"
                            >
                              ↓
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveClassTestCase(testCaseIndex)
                              }
                              className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Expandable content */}
                        {isExpanded && (
                          <div className="border-t border-border p-4 space-y-4">
                            {/* Intent (optional) */}
                            <div>
                              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                Intent (optional)
                              </label>
                              <Input
                                type="text"
                                value={classTestCase.intent || ''}
                                onChange={(e) =>
                                  handleUpdateClassTestCase(testCaseIndex, {
                                    intent: e.target.value,
                                  })
                                }
                                placeholder="Short description of what this test verifies"
                                className="w-full text-sm"
                              />
                            </div>

                            {/* Stale override warning */}
                            {classTestCase.displayOverrideStale && (
                              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
                                Input/output data has changed since the display
                                override was set. Review the override.
                              </div>
                            )}

                            {/* Input Display Override */}
                            <div>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground mb-1 text-xs underline"
                                onClick={() =>
                                  handleUpdateClassTestCase(testCaseIndex, {
                                    inputDisplayOverride:
                                      classTestCase.inputDisplayOverride ===
                                      undefined
                                        ? ''
                                        : undefined,
                                  })
                                }
                              >
                                {classTestCase.inputDisplayOverride !==
                                undefined
                                  ? 'Remove input display override'
                                  : 'Add input display override'}
                              </button>
                              {classTestCase.inputDisplayOverride !==
                                undefined && (
                                <Textarea
                                  value={classTestCase.inputDisplayOverride}
                                  onChange={(e) =>
                                    handleUpdateClassTestCase(testCaseIndex, {
                                      inputDisplayOverride: e.target.value,
                                      displayOverrideStale: false,
                                    })
                                  }
                                  placeholder="Formatted input for user display (supports comments, annotations)"
                                  className="min-h-[80px] w-full resize-y font-mono text-sm"
                                />
                              )}
                            </div>

                            {/* Output Display Override */}
                            <div>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground mb-1 text-xs underline"
                                onClick={() =>
                                  handleUpdateClassTestCase(testCaseIndex, {
                                    outputDisplayOverride:
                                      classTestCase.outputDisplayOverride ===
                                      undefined
                                        ? ''
                                        : undefined,
                                  })
                                }
                              >
                                {classTestCase.outputDisplayOverride !==
                                undefined
                                  ? 'Remove output display override'
                                  : 'Add output display override'}
                              </button>
                              {classTestCase.outputDisplayOverride !==
                                undefined && (
                                <Textarea
                                  value={classTestCase.outputDisplayOverride}
                                  onChange={(e) =>
                                    handleUpdateClassTestCase(testCaseIndex, {
                                      outputDisplayOverride: e.target.value,
                                      displayOverrideStale: false,
                                    })
                                  }
                                  placeholder="Formatted output for user display"
                                  className="min-h-[60px] w-full resize-y font-mono text-sm"
                                />
                              )}
                            </div>

                            {/* Image filename */}
                            <div>
                              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                Image (optional)
                              </label>
                              <ImageFilenameInput
                                value={classTestCase.imageFilename || ''}
                                onChange={(value) =>
                                  handleUpdateClassTestCase(testCaseIndex, {
                                    imageFilename: value || undefined,
                                  })
                                }
                                availableImages={availableImages}
                              />
                            </div>

                            {/* Constructor Args (if constructor has parameters) */}
                            {constructorParameters.length > 0 && (
                              <div>
                                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                  Constructor Arguments (JSON array)
                                </label>
                                <Input
                                  type="text"
                                  value={classTestCase.constructorArgs || '[]'}
                                  onChange={(e) =>
                                    handleUpdateClassTestCase(testCaseIndex, {
                                      constructorArgs: e.target.value,
                                    })
                                  }
                                  placeholder={`e.g., [${constructorParameters.map((p) => `<${p.name}>`).join(', ')}]`}
                                  className="w-full font-mono text-sm"
                                />
                              </div>
                            )}

                            {/* Operations */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="text-muted-foreground text-xs font-medium">
                                  Operations & Comments
                                </label>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleAddComment(testCaseIndex)
                                    }
                                    className="h-7 text-xs"
                                  >
                                    <MessageSquare className="mr-1 h-3 w-3" />
                                    Add Comment
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleAddOperation(testCaseIndex)
                                    }
                                    className="h-7 text-xs"
                                  >
                                    <Plus className="mr-1 h-3 w-3" />
                                    Add Operation
                                  </Button>
                                </div>
                              </div>

                              {classTestCase.operations.length === 0 ? (
                                <div className="border-border bg-muted/50 rounded border border-dashed p-4 text-center">
                                  <p className="text-muted-foreground text-xs">
                                    No operations defined. Add method calls or
                                    comments.
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {/* Operations Header - only shown when there are operations */}
                                  {classTestCase.operations.some(
                                    (op) => !isComment(op),
                                  ) && (
                                    <div className="text-muted-foreground grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 px-1 text-xs font-medium">
                                      <span>Method</span>
                                      <span>Arguments</span>
                                      <span>Expected</span>
                                      <span className="w-14 text-center">
                                        Error?
                                      </span>
                                      <span className="w-20"></span>
                                    </div>
                                  )}
                                  {classTestCase.operations.map(
                                    (op, opIndex) =>
                                      isComment(op) ? (
                                        /* Comment Item */
                                        <CommentRow
                                          // biome-ignore lint/suspicious/noArrayIndexKey: Operations don't have stable IDs
                                          key={`comment-${testCaseIndex}-${opIndex}`}
                                          text={op.text}
                                          onTextChange={(text) =>
                                            handleUpdateOperation(
                                              testCaseIndex,
                                              opIndex,
                                              {
                                                type: 'comment',
                                                text,
                                              },
                                            )
                                          }
                                          onMoveUp={() =>
                                            handleMoveOperation(
                                              testCaseIndex,
                                              opIndex,
                                              'up',
                                            )
                                          }
                                          onMoveDown={() =>
                                            handleMoveOperation(
                                              testCaseIndex,
                                              opIndex,
                                              'down',
                                            )
                                          }
                                          onRemove={() =>
                                            handleRemoveOperation(
                                              testCaseIndex,
                                              opIndex,
                                            )
                                          }
                                          isFirst={opIndex === 0}
                                          isLast={
                                            opIndex ===
                                            classTestCase.operations.length - 1
                                          }
                                        />
                                      ) : (
                                        /* Operation Item */
                                        <div
                                          // biome-ignore lint/suspicious/noArrayIndexKey: Operations don't have stable IDs
                                          key={`op-${testCaseIndex}-${opIndex}`}
                                          className="border-border bg-background rounded border p-2 space-y-2"
                                        >
                                          {/* Main operation row */}
                                          <div className="grid grid-cols-[1fr_1fr_1fr_auto_auto] items-start gap-2">
                                            <Input
                                              type="text"
                                              value={op.method}
                                              onChange={(e) =>
                                                handleUpdateOperation(
                                                  testCaseIndex,
                                                  opIndex,
                                                  { method: e.target.value },
                                                )
                                              }
                                              placeholder="method"
                                              className="h-8 font-mono text-sm"
                                            />
                                            <Input
                                              type="text"
                                              value={op.args}
                                              onChange={(e) =>
                                                handleUpdateOperation(
                                                  testCaseIndex,
                                                  opIndex,
                                                  { args: e.target.value },
                                                )
                                              }
                                              placeholder="[arg1, arg2]"
                                              className="h-8 font-mono text-sm"
                                            />
                                            <Textarea
                                              value={op.expected}
                                              onChange={(e) =>
                                                handleUpdateOperation(
                                                  testCaseIndex,
                                                  opIndex,
                                                  { expected: e.target.value },
                                                )
                                              }
                                              placeholder="null"
                                              className="min-h-[32px] max-h-[200px] font-mono text-sm resize-y"
                                              disabled={op.expectsError}
                                              title={
                                                op.expectsError
                                                  ? 'Disabled when expecting error'
                                                  : 'Supports multi-line output'
                                              }
                                            />
                                            <div className="flex w-14 items-center justify-center">
                                              <input
                                                type="checkbox"
                                                checked={
                                                  op.expectsError || false
                                                }
                                                onChange={(e) =>
                                                  handleUpdateOperation(
                                                    testCaseIndex,
                                                    opIndex,
                                                    {
                                                      expectsError:
                                                        e.target.checked,
                                                    },
                                                  )
                                                }
                                                className="h-4 w-4 rounded border-gray-300"
                                                title="Check if this operation should raise an error"
                                              />
                                            </div>
                                            <div className="flex w-20 items-center justify-end gap-1">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleMoveOperation(
                                                    testCaseIndex,
                                                    opIndex,
                                                    'up',
                                                  )
                                                }
                                                disabled={opIndex === 0}
                                                className="h-6 w-6 p-0"
                                              >
                                                ↑
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleMoveOperation(
                                                    testCaseIndex,
                                                    opIndex,
                                                    'down',
                                                  )
                                                }
                                                disabled={
                                                  opIndex ===
                                                  classTestCase.operations
                                                    .length -
                                                    1
                                                }
                                                className="h-6 w-6 p-0"
                                              >
                                                ↓
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleRemoveOperation(
                                                    testCaseIndex,
                                                    opIndex,
                                                  )
                                                }
                                                className="text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                          {/* Property verification row (shown when property check exists) */}
                                          {(op.propertyToCheck !== undefined ||
                                            op.expectedPropertyValue !==
                                              undefined) && (
                                            <div className="border-t border-border pt-2 mt-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-muted-foreground text-xs whitespace-nowrap">
                                                  Verify property:
                                                </span>
                                                <Input
                                                  type="text"
                                                  value={
                                                    op.propertyToCheck || ''
                                                  }
                                                  onChange={(e) =>
                                                    handleUpdateOperation(
                                                      testCaseIndex,
                                                      opIndex,
                                                      {
                                                        propertyToCheck:
                                                          e.target.value ||
                                                          undefined,
                                                      },
                                                    )
                                                  }
                                                  placeholder="property name"
                                                  className="h-7 w-32 font-mono text-xs"
                                                />
                                                <span className="text-muted-foreground text-xs">
                                                  =
                                                </span>
                                                <Input
                                                  type="text"
                                                  value={
                                                    op.expectedPropertyValue ||
                                                    ''
                                                  }
                                                  onChange={(e) =>
                                                    handleUpdateOperation(
                                                      testCaseIndex,
                                                      opIndex,
                                                      {
                                                        expectedPropertyValue:
                                                          e.target.value ||
                                                          undefined,
                                                      },
                                                    )
                                                  }
                                                  placeholder="expected value (JSON)"
                                                  className="h-7 flex-1 font-mono text-xs"
                                                />
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleUpdateOperation(
                                                      testCaseIndex,
                                                      opIndex,
                                                      {
                                                        propertyToCheck:
                                                          undefined,
                                                        expectedPropertyValue:
                                                          undefined,
                                                      },
                                                    )
                                                  }
                                                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                                  title="Remove property check"
                                                >
                                                  <Trash2 className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          )}
                                          {/* Add property check button (only if not already present) */}
                                          {op.propertyToCheck === undefined &&
                                            op.expectedPropertyValue ===
                                              undefined &&
                                            !op.expectsError && (
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                  handleUpdateOperation(
                                                    testCaseIndex,
                                                    opIndex,
                                                    {
                                                      propertyToCheck: '',
                                                      expectedPropertyValue: '',
                                                    },
                                                  )
                                                }
                                                className="h-6 text-xs text-muted-foreground hover:text-foreground"
                                              >
                                                <Plus className="mr-1 h-3 w-3" />
                                                Add property check
                                              </Button>
                                            )}
                                        </div>
                                      ),
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Test execution result */}
                            {testRunResults.get(testCaseIndex) && (
                              <div
                                className={cn(
                                  'rounded-md border p-2 text-xs',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'pass' &&
                                    'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'fail' &&
                                    'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
                                  testRunResults.get(testCaseIndex)!.status ===
                                    'error' &&
                                    'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
                                )}
                              >
                                <div className="font-medium">
                                  {testRunResults.get(testCaseIndex)!.status ===
                                  'pass'
                                    ? 'Test Passed'
                                    : 'Test Failed'}
                                </div>
                                {testRunResults.get(testCaseIndex)!.message && (
                                  <div className="mt-1 font-mono text-xs opacity-80">
                                    {testRunResults.get(testCaseIndex)!.message}
                                  </div>
                                )}
                                {testRunResults.get(testCaseIndex)!.status ===
                                  'fail' &&
                                  testRunResults.get(testCaseIndex)!.actual && (
                                    <div className="mt-1 font-mono">
                                      <span className="text-muted-foreground">
                                        Got:{' '}
                                      </span>
                                      {
                                        testRunResults.get(testCaseIndex)!
                                          .actual
                                      }
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            ) : /* Function-Based Test Cases */
            inputParameters.length === 0 ? (
              <div className="border-border bg-muted/50 rounded-lg border border-dashed p-6">
                <p className="text-muted-foreground text-center text-sm">
                  Define input parameters first before adding test cases.
                </p>
              </div>
            ) : editMode === 'text' ? (
              /* Text Mode */
              <div className="space-y-3">
                <div className="border-border bg-muted/30 rounded-lg border p-4">
                  <div className="mb-2">
                    <p className="text-muted-foreground text-xs">
                      <strong>Format:</strong> Each test case can optionally
                      start with{' '}
                      <code className="bg-muted rounded px-1">Intent: ...</code>
                      , followed by {inputParameters.length} input value
                      {inputParameters.length !== 1 ? 's' : ''} (
                      {inputParameters.map((p) => p.name).join(', ')}), then
                      expected output.
                    </p>
                  </div>
                  <div className="relative">
                    <Textarea
                      value={textModeContent}
                      onChange={(e) => handleTextChange(e.target.value)}
                      placeholder={`Example:\nIntent: Edge case description\n${inputParameters.map((p) => `<${p.name}>`).join('\n')}\n<expected output>`}
                      className="min-h-[300px] w-full resize-y font-mono text-sm"
                      style={{
                        lineHeight: '1.5',
                        tabSize: 2,
                      }}
                    />
                  </div>
                  <div className="text-muted-foreground mt-2 text-xs">
                    {testCases.length} test case
                    {testCases.length !== 1 ? 's' : ''} detected
                  </div>
                </div>
              </div>
            ) : testCases.length === 0 ? (
              /* UI Mode - Empty State */
              <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                <p className="text-muted-foreground mb-4">
                  No test cases added yet.
                </p>
                <Button
                  type="button"
                  onClick={handleAddTestCase}
                  variant="outline"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Test Case
                </Button>
              </div>
            ) : (
              /* UI Mode - Test Cases List */
              <div className="space-y-4">
                {testCases.map((testCase, index) => {
                  const isExpanded = expandedTestCases.has(index)
                  const inputPreview = inputParameters
                    .map((p) => testCase.inputs[p.name] || '?')
                    .join(', ')
                  const truncatedInput =
                    inputPreview.length > 60
                      ? `${inputPreview.substring(0, 57)}...`
                      : inputPreview

                  return (
                    <div
                      key={testCase.id || `test-${index}`}
                      className={cn(
                        'border-border rounded-lg border',
                        testCase.showAsExample &&
                          'border-l-4 border-l-blue-500',
                      )}
                    >
                      {/* Always-visible header */}
                      <div
                        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => toggleTestCase(index)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium flex-shrink-0">
                          #{index + 1}
                        </span>
                        {testCase.showAsExample && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex-shrink-0">
                            Example
                          </span>
                        )}
                        {/* Preview when collapsed */}
                        {!isExpanded && (
                          <span className="text-muted-foreground text-xs font-mono truncate">
                            {testCase.intent
                              ? testCase.intent
                              : `(${truncatedInput}) \u2192 ${(testCase.expectedOutput || '?').substring(0, 30)}`}
                          </span>
                        )}
                        {/* Action buttons */}
                        <div
                          className="ml-auto flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Result indicator */}
                          {testRunResults.get(index) && (
                            <span
                              className={cn(
                                'rounded px-1.5 py-0.5 text-xs font-medium',
                                testRunResults.get(index)!.status === 'pass' &&
                                  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                                testRunResults.get(index)!.status === 'fail' &&
                                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                                testRunResults.get(index)!.status === 'error' &&
                                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                              )}
                            >
                              {testRunResults.get(index)!.status === 'pass'
                                ? 'Pass'
                                : testRunResults.get(index)!.status === 'fail'
                                  ? 'Fail'
                                  : 'Err'}
                            </span>
                          )}
                          {/* Play button */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRunSingleTest(index)}
                            disabled={runningTestIndex !== null || runningAll}
                            className="h-6 w-6 p-0"
                            title="Run this test case"
                          >
                            {runningTestIndex === index ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={testCase.showAsExample || false}
                              onChange={(e) =>
                                handleUpdateTestCase(index, {
                                  showAsExample: e.target.checked,
                                })
                              }
                              className="h-3 w-3 rounded border-gray-300"
                            />
                            <span className="text-muted-foreground text-xs">
                              Ex
                            </span>
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveTestCase(index, 'up')}
                            disabled={index === 0}
                            className="h-7 w-7 p-0"
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveTestCase(index, 'down')}
                            disabled={index === testCases.length - 1}
                            className="h-7 w-7 p-0"
                          >
                            ↓
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTestCase(index)}
                            className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Expandable content */}
                      {isExpanded && (
                        <div className="border-t border-border p-4 space-y-3">
                          {/* Intent (optional) */}
                          <div>
                            <label className="text-muted-foreground mb-1 block text-xs font-medium">
                              Intent (optional)
                            </label>
                            <Input
                              type="text"
                              value={testCase.intent || ''}
                              onChange={(e) =>
                                handleUpdateTestCase(index, {
                                  intent: e.target.value,
                                })
                              }
                              placeholder="Short rationale for this test case"
                              className="w-full text-sm"
                            />
                          </div>

                          {/* Stale override warning */}
                          {testCase.displayOverrideStale && (
                            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
                              Input/output data has changed since the display
                              override was set. Review the override.
                            </div>
                          )}

                          {/* Input Display Override */}
                          <div>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground mb-1 text-xs underline"
                              onClick={() =>
                                handleUpdateTestCase(index, {
                                  inputDisplayOverride:
                                    testCase.inputDisplayOverride === undefined
                                      ? ''
                                      : undefined,
                                })
                              }
                            >
                              {testCase.inputDisplayOverride !== undefined
                                ? 'Remove input display override'
                                : 'Add input display override'}
                            </button>
                            {testCase.inputDisplayOverride !== undefined && (
                              <Textarea
                                value={testCase.inputDisplayOverride}
                                onChange={(e) =>
                                  handleUpdateTestCase(index, {
                                    inputDisplayOverride: e.target.value,
                                    displayOverrideStale: false,
                                  })
                                }
                                placeholder="Formatted input for user display (supports comments, annotations)"
                                className="min-h-[80px] w-full resize-y font-mono text-sm"
                              />
                            )}
                          </div>

                          {/* Output Display Override */}
                          <div>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground mb-1 text-xs underline"
                              onClick={() =>
                                handleUpdateTestCase(index, {
                                  outputDisplayOverride:
                                    testCase.outputDisplayOverride === undefined
                                      ? ''
                                      : undefined,
                                })
                              }
                            >
                              {testCase.outputDisplayOverride !== undefined
                                ? 'Remove output display override'
                                : 'Add output display override'}
                            </button>
                            {testCase.outputDisplayOverride !== undefined && (
                              <Textarea
                                value={testCase.outputDisplayOverride}
                                onChange={(e) =>
                                  handleUpdateTestCase(index, {
                                    outputDisplayOverride: e.target.value,
                                    displayOverrideStale: false,
                                  })
                                }
                                placeholder="Formatted output for user display"
                                className="min-h-[60px] w-full resize-y font-mono text-sm"
                              />
                            )}
                          </div>

                          {/* Image filename */}
                          <div>
                            <label className="text-muted-foreground mb-1 block text-xs font-medium">
                              Image (optional)
                            </label>
                            <ImageFilenameInput
                              value={testCase.imageFilename || ''}
                              onChange={(value) =>
                                handleUpdateTestCase(index, {
                                  imageFilename: value || undefined,
                                })
                              }
                              availableImages={availableImages}
                            />
                          </div>

                          {/* Input values */}
                          {inputParameters.map((param) => (
                            <div key={param.name}>
                              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                {param.name} ({param.type})
                              </label>
                              {param.type === 'function' ? (
                                <FunctionInput
                                  value={testCase.inputs[param.name] || ''}
                                  onChange={(value) =>
                                    handleUpdateTestCase(index, {
                                      inputs: {
                                        ...testCase.inputs,
                                        [param.name]: value,
                                      },
                                    })
                                  }
                                  paramName={param.name}
                                />
                              ) : (
                                <Textarea
                                  value={testCase.inputs[param.name] || ''}
                                  onChange={(e) =>
                                    handleUpdateTestCase(index, {
                                      inputs: {
                                        ...testCase.inputs,
                                        [param.name]: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder={`Value for ${param.name}`}
                                  className="min-h-[40px] w-full resize-y font-mono text-sm"
                                />
                              )}
                            </div>
                          ))}

                          {/* Expected output */}
                          <div>
                            <label className="text-muted-foreground mb-1 block text-xs font-medium">
                              Expected Output ({outputType})
                            </label>
                            <Textarea
                              value={testCase.expectedOutput}
                              onChange={(e) =>
                                handleUpdateTestCase(index, {
                                  expectedOutput: e.target.value,
                                })
                              }
                              placeholder="Expected output value"
                              className="min-h-[40px] w-full resize-y font-mono text-sm"
                            />
                          </div>

                          {/* Expected State After (for in-place modifications) */}
                          {inputParameters.some((p) => p.inPlaceModified) && (
                            <div className="border-t border-border pt-3 mt-3">
                              <h4 className="text-xs font-medium text-muted-foreground mb-2">
                                Expected State After (in-place modifications)
                              </h4>
                              {inputParameters
                                .filter((p) => p.inPlaceModified)
                                .map((param) => (
                                  <div
                                    key={`after-${param.name}`}
                                    className="mb-2"
                                  >
                                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                                      {param.name} after ({param.type})
                                    </label>
                                    <Textarea
                                      value={
                                        testCase.expectedInputsAfter?.[
                                          param.name
                                        ] || ''
                                      }
                                      onChange={(e) =>
                                        handleUpdateTestCase(index, {
                                          expectedInputsAfter: {
                                            ...testCase.expectedInputsAfter,
                                            [param.name]: e.target.value,
                                          },
                                        })
                                      }
                                      placeholder={`Expected value of ${param.name} after function call`}
                                      className="min-h-[40px] w-full resize-y font-mono text-sm"
                                    />
                                  </div>
                                ))}
                            </div>
                          )}

                          {/* Test execution result */}
                          {testRunResults.get(index) && (
                            <div
                              className={cn(
                                'rounded-md border p-2 text-xs',
                                testRunResults.get(index)!.status === 'pass' &&
                                  'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20',
                                testRunResults.get(index)!.status === 'fail' &&
                                  'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
                                testRunResults.get(index)!.status === 'error' &&
                                  'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
                              )}
                            >
                              <div className="font-medium">
                                {testRunResults.get(index)!.status === 'pass'
                                  ? 'Test Passed'
                                  : 'Test Failed'}
                              </div>
                              {testRunResults.get(index)!.message && (
                                <div className="mt-1 font-mono text-xs opacity-80">
                                  {testRunResults.get(index)!.message}
                                </div>
                              )}
                              {testRunResults.get(index)!.status === 'fail' &&
                                testRunResults.get(index)!.actual && (
                                  <div className="mt-1 font-mono">
                                    <span className="text-muted-foreground">
                                      Got:{' '}
                                    </span>
                                    {testRunResults.get(index)!.actual}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
